var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/internal/deployEnv.ts
var getDeployEnv = () => {
  var _a, _b, _c, _d;
  if (typeof window === "undefined" || typeof document === "undefined") {
    return "development";
  }
  const hostname = (_a = window == null ? void 0 : window.location) == null ? void 0 : _a.hostname;
  if (hostname === void 0) {
    return "development";
  }
  const port = (_b = window == null ? void 0 : window.location) == null ? void 0 : _b.port;
  const locationHasPort = port && port !== "";
  const onLocalhost = hostname === "localhost";
  const onNgrok = hostname.includes(".ngrok.io");
  const onXipIO = hostname.includes(".xip.io");
  const inAndroidEmulator = hostname.includes("10.0.2.2");
  const inDevelopment = onLocalhost && locationHasPort || onNgrok || onXipIO || inAndroidEmulator;
  if (inDevelopment) {
    return "development";
  }
  const inRailsCI = hostname === "127.0.0.1";
  if (inRailsCI || onLocalhost) {
    return "test";
  }
  const onStagingManagedDomain = hostname.includes("-staging.managed.services.opendoor.com");
  const onSimplersell = hostname.includes(".simplersell.com");
  const onODMortgage = hostname.includes(".odmortgage.com");
  const inStaging = onStagingManagedDomain || onSimplersell || onODMortgage;
  const onOpendoorHomeLoans = hostname.includes("opendoorhomeloans.com");
  const onOpendoor = hostname.includes(".opendoor.com") && !hostname.includes("-staging");
  const inProduction = onOpendoor || onOpendoorHomeLoans;
  const inProdOrStaging = inStaging || inProduction;
  const onQADomain = hostname.includes("-qa-");
  const hasQACookie = ((_c = document == null ? void 0 : document.cookie) == null ? void 0 : _c.includes("qa=")) && ((_d = document == null ? void 0 : document.cookie) == null ? void 0 : _d.includes("qa_env="));
  const inQA = onQADomain || inProdOrStaging && hasQACookie;
  if (inQA) {
    return "qa";
  } else if (inStaging) {
    return "staging";
  } else if (inProduction) {
    return "production";
  }
  return "development";
};

// src/contextualData.ts
var emptyContextualData = {
  user: void 0,
  data: {},
  adClickContext: void 0,
  campaignContext: void 0,
  customerContext: void 0,
  exclusiveOffersContext: void 0,
  leadContext: void 0,
  sellDirectContext: void 0
};
var emptyJourneyEventContextualData = {
  customerContext: void 0,
  campaignContext: void 0
};
function mergeContextualData(parent, child = emptyContextualData) {
  if (getDeployEnv() !== "production" && parent.user && child.user) {
    throw new Error("User already set. This error only shows in non-production environments.");
  }
  return {
    user: child.user || parent.user,
    data: { ...parent.data, ...child.data },
    campaignContext: child.campaignContext || parent.campaignContext,
    customerContext: child.customerContext || parent.customerContext,
    exclusiveOffersContext: child.exclusiveOffersContext || parent.exclusiveOffersContext,
    leadContext: child.leadContext || parent.leadContext,
    sellDirectContext: child.sellDirectContext || parent.sellDirectContext,
    adClickContext: child.adClickContext || parent.adClickContext
  };
}

// src/global.ts
var ProxyObservabilityInstance = class {
  currentInstance = null;
  setCurrentInstance(instance) {
    this.currentInstance = instance;
  }
  // This is only used for testing.
  _destroyCurrentInstance() {
    this.currentInstance = null;
  }
  /**
   * Track events not performed by a user but meaningful internally. For events performed
   * by a user, use `trackUserEvent`.
   *
   * TODO (@core-experience-platform): rename to `trackInternalEvent`
   */
  trackEvent(action, label, contextualData, options) {
    var _a;
    (_a = this.currentInstance) == null ? void 0 : _a.trackEvent(action, label, contextualData, options);
  }
  /**
   * @deprecated Use trackJourneyEvent instead
   * @see https://opendoor.atlassian.net/wiki/spaces/CONSUMER/pages/2283536444/How+to+Create+Journey+2.0+Events
   */
  trackUserEvent(event, contextualData) {
    var _a;
    (_a = this.currentInstance) == null ? void 0 : _a.trackUserEvent(event, contextualData);
  }
  trackJourneyEvent(categoryName, eventName, eventData, contextualData = emptyJourneyEventContextualData) {
    var _a;
    (_a = this.currentInstance) == null ? void 0 : _a.trackJourneyEvent(categoryName, eventName, eventData, contextualData);
  }
  trackError(label, error, contextualData, options) {
    var _a;
    (_a = this.currentInstance) == null ? void 0 : _a.trackError(label, error, contextualData, options);
  }
  // All of the "getXXXClient" methods return a Proxy object, that capture requests
  // and then re-evaluate and forward the request to the currentInstance every time.
  // This ensures that if the currentInstance is changed, the new instance will be used.
  // And it also allows for behavior like:
  // import { Sentry } from '@opendoor/observability';
  // If you don't do this, and Sentry is delayed in initializing, then the user will
  // have a reference to an empty object forever, instead of a reference to an object
  // that will eventually have methods in it.
  // The only downside of this is that users have to handle undefined for the methods
  // but that tradeoff is okay.
  getDatadogRumClient() {
    return new Proxy(
      {},
      {
        get: (_, prop) => {
          return this.currentInstance ? this.currentInstance.getDatadogRumClient()[prop] : void 0;
        }
      }
    );
  }
  getJourneyUserClient() {
    return new Proxy(
      {},
      {
        get: (_, prop) => {
          return this.currentInstance ? this.currentInstance.getJourneyUserClient()[prop] : void 0;
        },
        set: (_, prop, value) => {
          return this.currentInstance ? this.currentInstance.getJourneyUserClient()[prop] = value : void 0;
        }
      }
    );
  }
  getJourneyEventClient() {
    return new Proxy(
      {},
      {
        get: (_, prop) => {
          return this.currentInstance ? this.currentInstance.getJourneyEventClient()[prop] : void 0;
        },
        set: (_, prop, value) => {
          return this.currentInstance ? this.currentInstance.getJourneyEventClient()[prop] = value : void 0;
        }
      }
    );
  }
  getSentryClient() {
    return new Proxy(
      {},
      {
        get: (_, prop) => this.currentInstance ? this.currentInstance.getSentryClient()[prop] : void 0
      }
    );
  }
  isInitialized() {
    return this.currentInstance !== null;
  }
};
var globalObservability = new ProxyObservabilityInstance();

// src/internal/sentry.ts
function buildDefaultSentryOpts(opts) {
  const { env, appName, version } = opts;
  return {
    environment: env,
    release: `${appName}@${version}`,
    ignoreErrors: [...DEFAULT_IGNORE_ERRORS],
    beforeSend
  };
}
function trackEventInSentry(action, label, contextualData = emptyContextualData) {
  var _a, _b;
  (_b = (_a = globalObservability.getSentryClient()).addBreadcrumb) == null ? void 0 : _b.call(_a, {
    category: action,
    message: label,
    data: {
      ...contextualData.data,
      adClickContext: contextualData.adClickContext,
      campaignContext: contextualData.campaignContext,
      customerContext: contextualData.customerContext,
      exclusiveOffersContext: contextualData.exclusiveOffersContext,
      leadContext: contextualData.leadContext,
      sellDirectContext: contextualData.sellDirectContext
    },
    timestamp: Date.now()
  });
}
function trackUserEventInSentry(event, contextualData = emptyContextualData) {
  var _a, _b;
  (_b = (_a = globalObservability.getSentryClient()).addBreadcrumb) == null ? void 0 : _b.call(_a, {
    category: Object.keys(event)[0] ?? "unknownUserEvent",
    data: {
      ...event,
      ...contextualData.data,
      adClickContext: contextualData.adClickContext,
      campaignContext: contextualData.campaignContext,
      customerContext: contextualData.customerContext,
      exclusiveOffersContext: contextualData.exclusiveOffersContext,
      leadContext: contextualData.leadContext,
      sellDirectContext: contextualData.sellDirectContext
    },
    timestamp: Date.now()
  });
}
function trackJourneyEventInSentry(event, contextualData, journeyEventHelpers) {
  var _a, _b, _c;
  const category = ((_a = event.category) == null ? void 0 : _a.$case) ?? "unknownUserEvent";
  const journeyEventJSON = journeyEventHelpers.toJSON(event);
  (_c = (_b = globalObservability.getSentryClient()).addBreadcrumb) == null ? void 0 : _c.call(_b, {
    category,
    data: {
      ...journeyEventJSON,
      campaignContext: contextualData.campaignContext
    },
    timestamp: Date.now()
  });
}
function trackErrorInSentry(label, error, contextualData = emptyContextualData) {
  var _a, _b;
  let newContextualData = emptyContextualData;
  (_b = (_a = globalObservability.getSentryClient()).withScope) == null ? void 0 : _b.call(_a, (scope) => {
    var _a2, _b2;
    scope.setFingerprint([label]);
    scope.setUser(
      contextualData.user ? {
        ...contextualData.user,
        id: contextualData.user.humanID
      } : null
    );
    scope.setTags({
      ...contextualDataToTags(contextualData.data),
      label
      // add label as a tag so we can search for it
    });
    scope.setExtras(contextualData.data);
    const eventId = (_b2 = (_a2 = globalObservability.getSentryClient()).captureException) == null ? void 0 : _b2.call(_a2, error || new Error(label));
    newContextualData = {
      data: {
        "sentry-event-id": eventId
      }
    };
  });
  return newContextualData;
}
function contextualDataToTags(data) {
  let tags = {};
  Object.keys(data).forEach((key) => {
    const value = data[key];
    switch (typeof value) {
      case "undefined":
      case "boolean":
      case "number":
      case "bigint":
      case "string":
      case "symbol":
        tags[key] = String(value);
        break;
      case "object":
        if (value === null) {
          tags[key] = "null";
        }
        break;
    }
  });
  return tags;
}
var DEFAULT_IGNORE_ERRORS = [
  // Error generated by a bug in auto-fill library from Chrome Mobile on iOS
  // https://github.com/getsentry/sentry/issues/5267
  "Blocked a frame with origin",
  // error generated by microsoft outlook's safelink crawler on pages we email out
  // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
  "Object Not Found Matching Id",
  // Error that MAY indicate a performance issue due to browser implementation, but has been shown to be safe to ignore.
  // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications"
];
var beforeSendFn = (mappers, event) => {
  return mappers.reduce((acc, mapper) => mapper(acc), event);
};
var beforeSend = (event) => {
  if (!event) {
    return null;
  }
  return beforeSendFn(MAPPERS, event);
};
var isPhantomJs = (event) => {
  if (typeof window === "undefined") {
    return event;
  }
  const isPhantomJS = /PhantomJS/.test(window.navigator.userAgent);
  return !isPhantomJS ? event : null;
};
var isInvalidArgumentError = (event) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const firstFrameFilename = ((_f = (_e = (_d = (_c = (_b = (_a = event == null ? void 0 : event.exception) == null ? void 0 : _a.values) == null ? void 0 : _b[0]) == null ? void 0 : _c.stacktrace) == null ? void 0 : _d.frames) == null ? void 0 : _e[0]) == null ? void 0 : _f.filename) || "";
  const eventMessage = ((_i = (_h = (_g = event == null ? void 0 : event.exception) == null ? void 0 : _g.values) == null ? void 0 : _h[0]) == null ? void 0 : _i.value) || "";
  if (eventMessage.includes("Invalid argument") && firstFrameFilename.includes("maps-api-v3")) {
    return null;
  }
  if (eventMessage.includes('Could not load "map"') || eventMessage.includes('Could not load "marker"') || // Error occurs when watching Vimeo videos on Safari
  eventMessage.includes("The operation was aborted.")) {
    return null;
  }
  return event;
};
var MAPPERS = [isPhantomJs, isInvalidArgumentError];

// src/internal/datadog.ts
var buildDefaultDatadogOptions = (opts) => {
  const { appName, env, version } = opts;
  return {
    site: "datadoghq.com",
    version,
    env,
    service: appName
  };
};
function getContextualDataFromDatadog() {
  var _a, _b, _c;
  const sessionId = (_c = (_b = (_a = globalObservability.getDatadogRumClient()).getInternalContext) == null ? void 0 : _b.call(_a)) == null ? void 0 : _c.session_id;
  if (sessionId) {
    return {
      data: {
        "datadog-rum-session-id": sessionId,
        "datadog-rum-session-replay": `https://app.datadoghq.com/rum/replay/sessions/${sessionId}`,
        "datadog-rum-session-search": `https://app.datadoghq.com/rum/explorer?query=%40session.id%3A${sessionId}`
      }
    };
  } else {
    return emptyContextualData;
  }
}
function trackPerformanceInDatadog(eventName) {
  var _a, _b;
  (_b = (_a = globalObservability.getDatadogRumClient()).addTiming) == null ? void 0 : _b.call(_a, eventName);
}
function addRUMGlobalContext(context) {
  var _a, _b, _c, _d;
  (_d = (_a = globalObservability.getDatadogRumClient()) == null ? void 0 : _a.setRumGlobalContext) == null ? void 0 : _d.call(_a, {
    ...(_c = (_b = globalObservability.getDatadogRumClient()).getRumGlobalContext) == null ? void 0 : _c.call(_b),
    ...context
  });
}
function trackEventInDatadog(action, label, contextualData = emptyContextualData) {
  var _a, _b;
  (_b = (_a = globalObservability.getDatadogRumClient()).addAction) == null ? void 0 : _b.call(_a, action, {
    ...contextualData.data,
    adClickContext: contextualData.adClickContext,
    campaignContext: contextualData.campaignContext,
    customerContext: contextualData.customerContext,
    exclusiveOffersContext: contextualData.exclusiveOffersContext,
    leadContext: contextualData.leadContext,
    sellDirectContext: contextualData.sellDirectContext,
    label
  });
}
function trackUserEventInDatadog(event, contextualData = emptyContextualData) {
  var _a, _b, _c;
  (_c = (_b = globalObservability.getDatadogRumClient()).addAction) == null ? void 0 : _c.call(_b, ((_a = Object.keys(event)) == null ? void 0 : _a[0]) ?? "unknownUserEvent", {
    ...event,
    ...contextualData.data,
    adClickContext: contextualData.adClickContext,
    campaignContext: contextualData.campaignContext,
    customerContext: contextualData.customerContext,
    exclusiveOffersContext: contextualData.exclusiveOffersContext,
    leadContext: contextualData.leadContext,
    sellDirectContext: contextualData.sellDirectContext
  });
}
function trackJourneyEventInDatadog(event, contextualData, journeyEventHelpers) {
  var _a, _b, _c;
  const category = ((_a = event.category) == null ? void 0 : _a.$case) ?? "unknownUserEvent";
  const journeyEventJSON = journeyEventHelpers.toJSON(event);
  (_c = (_b = globalObservability.getDatadogRumClient()).addAction) == null ? void 0 : _c.call(_b, category, {
    ...journeyEventJSON,
    campaignContext: contextualData.campaignContext
  });
}
function trackErrorInDatadog(label, error, contextualData = emptyContextualData) {
  var _a, _b;
  if (shouldExcludeErrorByMessage(error)) {
    return;
  }
  (_b = (_a = globalObservability.getDatadogRumClient()).addError) == null ? void 0 : _b.call(_a, error || new Error(label), {
    ...contextualData.data,
    label
  });
}
var ERROR_MESSAGES_TO_EXCLUDE = [
  // Error that MAY indicate a performance issue due to browser implementation, but has been shown to be safe to ignore.
  // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  "ResizeObserver loop limit exceeded"
];
var shouldExcludeErrorByMessage = (error) => {
  return ERROR_MESSAGES_TO_EXCLUDE.includes((error == null ? void 0 : error.message) || "");
};

// src/internal/googleTagManager.ts
var loadGoogleTagManager = (lazyLoad) => {
  const injectScript = () => {
    if (typeof window !== "undefined") {
      (function(w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({
          "gtm.start": (/* @__PURE__ */ new Date()).getTime(),
          event: "gtm.js"
        });
        var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l !== "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "//www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, "script", "dataLayer", "GTM-MSX7NS");
    }
  };
  if (lazyLoad) {
    window.addEventListener("load", injectScript);
  } else {
    injectScript();
  }
};

// src/internal/journey.ts
import Cookies from "js-cookie";

// src/__generated__/protobuf/journey/data/sources.ts
function websiteSource_EventOriginFromJSON(object) {
  switch (object) {
    case 0:
    case "UNSPECIFIED":
      return 0 /* UNSPECIFIED */;
    case 1:
    case "SERVER":
      return 1 /* SERVER */;
    case 2:
    case "CLIENT":
      return 2 /* CLIENT */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function websiteSource_EventOriginToJSON(object) {
  switch (object) {
    case 0 /* UNSPECIFIED */:
      return "UNSPECIFIED";
    case 1 /* SERVER */:
      return "SERVER";
    case 2 /* CLIENT */:
      return "CLIENT";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}
function mobileSource_EventOriginFromJSON(object) {
  switch (object) {
    case 0:
    case "APP":
      return 0 /* APP */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function mobileSource_EventOriginToJSON(object) {
  switch (object) {
    case 0 /* APP */:
      return "APP";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}
function createBaseWebsiteSource() {
  return {
    url: "",
    userAgent: void 0,
    ip: void 0,
    referrer: void 0,
    proxyPath: void 0,
    pathname: void 0,
    queryMap: {},
    host: void 0,
    eventOrigin: 0,
    proxyQueryMap: {},
    screen: void 0,
    language: []
  };
}
var WebsiteSource = {
  fromJSON(object) {
    return {
      url: isSet(object.url) ? String(object.url) : "",
      userAgent: isSet(object.userAgent) ? String(object.userAgent) : void 0,
      ip: isSet(object.ip) ? String(object.ip) : void 0,
      referrer: isSet(object.referrer) ? String(object.referrer) : void 0,
      proxyPath: isSet(object.proxyPath) ? String(object.proxyPath) : void 0,
      pathname: isSet(object.pathname) ? String(object.pathname) : void 0,
      queryMap: isObject(object.queryMap) ? Object.entries(object.queryMap).reduce((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {}) : {},
      host: isSet(object.host) ? String(object.host) : void 0,
      eventOrigin: isSet(object.eventOrigin) ? websiteSource_EventOriginFromJSON(object.eventOrigin) : 0,
      proxyQueryMap: isObject(object.proxyQueryMap) ? Object.entries(object.proxyQueryMap).reduce((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {}) : {},
      screen: isSet(object.screen) ? WebsiteSource_Screen.fromJSON(object.screen) : void 0,
      language: Array.isArray(object == null ? void 0 : object.language) ? object.language.map((e) => String(e)) : []
    };
  },
  toJSON(message) {
    var _a;
    const obj = {};
    if (message.url !== "") {
      obj.url = message.url;
    }
    if (message.userAgent !== void 0) {
      obj.userAgent = message.userAgent;
    }
    if (message.ip !== void 0) {
      obj.ip = message.ip;
    }
    if (message.referrer !== void 0) {
      obj.referrer = message.referrer;
    }
    if (message.proxyPath !== void 0) {
      obj.proxyPath = message.proxyPath;
    }
    if (message.pathname !== void 0) {
      obj.pathname = message.pathname;
    }
    if (message.queryMap) {
      const entries = Object.entries(message.queryMap);
      if (entries.length > 0) {
        obj.queryMap = {};
        entries.forEach(([k, v]) => {
          obj.queryMap[k] = v;
        });
      }
    }
    if (message.host !== void 0) {
      obj.host = message.host;
    }
    if (message.eventOrigin !== 0) {
      obj.eventOrigin = websiteSource_EventOriginToJSON(message.eventOrigin);
    }
    if (message.proxyQueryMap) {
      const entries = Object.entries(message.proxyQueryMap);
      if (entries.length > 0) {
        obj.proxyQueryMap = {};
        entries.forEach(([k, v]) => {
          obj.proxyQueryMap[k] = v;
        });
      }
    }
    if (message.screen !== void 0) {
      obj.screen = WebsiteSource_Screen.toJSON(message.screen);
    }
    if ((_a = message.language) == null ? void 0 : _a.length) {
      obj.language = message.language;
    }
    return obj;
  },
  create(base) {
    return WebsiteSource.fromPartial(base ?? {});
  },
  fromPartial(object) {
    var _a;
    const message = createBaseWebsiteSource();
    message.url = object.url ?? "";
    message.userAgent = object.userAgent ?? void 0;
    message.ip = object.ip ?? void 0;
    message.referrer = object.referrer ?? void 0;
    message.proxyPath = object.proxyPath ?? void 0;
    message.pathname = object.pathname ?? void 0;
    message.queryMap = Object.entries(object.queryMap ?? {}).reduce((acc, [key, value]) => {
      if (value !== void 0) {
        acc[key] = String(value);
      }
      return acc;
    }, {});
    message.host = object.host ?? void 0;
    message.eventOrigin = object.eventOrigin ?? 0;
    message.proxyQueryMap = Object.entries(object.proxyQueryMap ?? {}).reduce(
      (acc, [key, value]) => {
        if (value !== void 0) {
          acc[key] = String(value);
        }
        return acc;
      },
      {}
    );
    message.screen = object.screen !== void 0 && object.screen !== null ? WebsiteSource_Screen.fromPartial(object.screen) : void 0;
    message.language = ((_a = object.language) == null ? void 0 : _a.map((e) => e)) || [];
    return message;
  }
};
function createBaseWebsiteSource_Screen() {
  return { density: 0, width: 0, height: 0 };
}
var WebsiteSource_Screen = {
  fromJSON(object) {
    return {
      density: isSet(object.density) ? Number(object.density) : 0,
      width: isSet(object.width) ? Number(object.width) : 0,
      height: isSet(object.height) ? Number(object.height) : 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.density !== 0) {
      obj.density = Math.round(message.density);
    }
    if (message.width !== 0) {
      obj.width = Math.round(message.width);
    }
    if (message.height !== 0) {
      obj.height = Math.round(message.height);
    }
    return obj;
  },
  create(base) {
    return WebsiteSource_Screen.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseWebsiteSource_Screen();
    message.density = object.density ?? 0;
    message.width = object.width ?? 0;
    message.height = object.height ?? 0;
    return message;
  }
};
function createBaseMobileSource() {
  return {
    userAgent: void 0,
    ip: void 0,
    appName: "",
    appBuild: void 0,
    appVersion: void 0,
    appNamespace: void 0,
    deviceId: "",
    deviceType: void 0,
    deviceModel: void 0,
    deviceManufacturer: void 0,
    deviceName: void 0,
    advertisingId: void 0,
    adTrackingEnabled: void 0,
    notificationPermissionStatus: void 0,
    carrier: void 0,
    osName: void 0,
    osVersion: void 0,
    screenDensity: void 0,
    screenHeight: void 0,
    screenWidth: void 0,
    focusRouteName: "",
    eventOrigin: 0
  };
}
var MobileSource = {
  fromJSON(object) {
    return {
      userAgent: isSet(object.userAgent) ? String(object.userAgent) : void 0,
      ip: isSet(object.ip) ? String(object.ip) : void 0,
      appName: isSet(object.appName) ? String(object.appName) : "",
      appBuild: isSet(object.appBuild) ? String(object.appBuild) : void 0,
      appVersion: isSet(object.appVersion) ? String(object.appVersion) : void 0,
      appNamespace: isSet(object.appNamespace) ? String(object.appNamespace) : void 0,
      deviceId: isSet(object.deviceId) ? String(object.deviceId) : "",
      deviceType: isSet(object.deviceType) ? String(object.deviceType) : void 0,
      deviceModel: isSet(object.deviceModel) ? String(object.deviceModel) : void 0,
      deviceManufacturer: isSet(object.deviceManufacturer) ? String(object.deviceManufacturer) : void 0,
      deviceName: isSet(object.deviceName) ? String(object.deviceName) : void 0,
      advertisingId: isSet(object.advertisingId) ? String(object.advertisingId) : void 0,
      adTrackingEnabled: isSet(object.adTrackingEnabled) ? Boolean(object.adTrackingEnabled) : void 0,
      notificationPermissionStatus: isSet(object.notificationPermissionStatus) ? String(object.notificationPermissionStatus) : void 0,
      carrier: isSet(object.carrier) ? String(object.carrier) : void 0,
      osName: isSet(object.osName) ? String(object.osName) : void 0,
      osVersion: isSet(object.osVersion) ? String(object.osVersion) : void 0,
      screenDensity: isSet(object.screenDensity) ? Number(object.screenDensity) : void 0,
      screenHeight: isSet(object.screenHeight) ? Number(object.screenHeight) : void 0,
      screenWidth: isSet(object.screenWidth) ? Number(object.screenWidth) : void 0,
      focusRouteName: isSet(object.focusRouteName) ? String(object.focusRouteName) : "",
      eventOrigin: isSet(object.eventOrigin) ? mobileSource_EventOriginFromJSON(object.eventOrigin) : 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.userAgent !== void 0) {
      obj.userAgent = message.userAgent;
    }
    if (message.ip !== void 0) {
      obj.ip = message.ip;
    }
    if (message.appName !== "") {
      obj.appName = message.appName;
    }
    if (message.appBuild !== void 0) {
      obj.appBuild = message.appBuild;
    }
    if (message.appVersion !== void 0) {
      obj.appVersion = message.appVersion;
    }
    if (message.appNamespace !== void 0) {
      obj.appNamespace = message.appNamespace;
    }
    if (message.deviceId !== "") {
      obj.deviceId = message.deviceId;
    }
    if (message.deviceType !== void 0) {
      obj.deviceType = message.deviceType;
    }
    if (message.deviceModel !== void 0) {
      obj.deviceModel = message.deviceModel;
    }
    if (message.deviceManufacturer !== void 0) {
      obj.deviceManufacturer = message.deviceManufacturer;
    }
    if (message.deviceName !== void 0) {
      obj.deviceName = message.deviceName;
    }
    if (message.advertisingId !== void 0) {
      obj.advertisingId = message.advertisingId;
    }
    if (message.adTrackingEnabled !== void 0) {
      obj.adTrackingEnabled = message.adTrackingEnabled;
    }
    if (message.notificationPermissionStatus !== void 0) {
      obj.notificationPermissionStatus = message.notificationPermissionStatus;
    }
    if (message.carrier !== void 0) {
      obj.carrier = message.carrier;
    }
    if (message.osName !== void 0) {
      obj.osName = message.osName;
    }
    if (message.osVersion !== void 0) {
      obj.osVersion = message.osVersion;
    }
    if (message.screenDensity !== void 0) {
      obj.screenDensity = message.screenDensity;
    }
    if (message.screenHeight !== void 0) {
      obj.screenHeight = message.screenHeight;
    }
    if (message.screenWidth !== void 0) {
      obj.screenWidth = message.screenWidth;
    }
    if (message.focusRouteName !== "") {
      obj.focusRouteName = message.focusRouteName;
    }
    if (message.eventOrigin !== 0) {
      obj.eventOrigin = mobileSource_EventOriginToJSON(message.eventOrigin);
    }
    return obj;
  },
  create(base) {
    return MobileSource.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseMobileSource();
    message.userAgent = object.userAgent ?? void 0;
    message.ip = object.ip ?? void 0;
    message.appName = object.appName ?? "";
    message.appBuild = object.appBuild ?? void 0;
    message.appVersion = object.appVersion ?? void 0;
    message.appNamespace = object.appNamespace ?? void 0;
    message.deviceId = object.deviceId ?? "";
    message.deviceType = object.deviceType ?? void 0;
    message.deviceModel = object.deviceModel ?? void 0;
    message.deviceManufacturer = object.deviceManufacturer ?? void 0;
    message.deviceName = object.deviceName ?? void 0;
    message.advertisingId = object.advertisingId ?? void 0;
    message.adTrackingEnabled = object.adTrackingEnabled ?? void 0;
    message.notificationPermissionStatus = object.notificationPermissionStatus ?? void 0;
    message.carrier = object.carrier ?? void 0;
    message.osName = object.osName ?? void 0;
    message.osVersion = object.osVersion ?? void 0;
    message.screenDensity = object.screenDensity ?? void 0;
    message.screenHeight = object.screenHeight ?? void 0;
    message.screenWidth = object.screenWidth ?? void 0;
    message.focusRouteName = object.focusRouteName ?? "";
    message.eventOrigin = object.eventOrigin ?? 0;
    return message;
  }
};
function createBaseBackendSource() {
  return { name: "", clientUserAgent: void 0, clientIp: void 0 };
}
var BackendSource = {
  fromJSON(object) {
    return {
      name: isSet(object.name) ? String(object.name) : "",
      clientUserAgent: isSet(object.clientUserAgent) ? String(object.clientUserAgent) : void 0,
      clientIp: isSet(object.clientIp) ? String(object.clientIp) : void 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.clientUserAgent !== void 0) {
      obj.clientUserAgent = message.clientUserAgent;
    }
    if (message.clientIp !== void 0) {
      obj.clientIp = message.clientIp;
    }
    return obj;
  },
  create(base) {
    return BackendSource.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseBackendSource();
    message.name = object.name ?? "";
    message.clientUserAgent = object.clientUserAgent ?? void 0;
    message.clientIp = object.clientIp ?? void 0;
    return message;
  }
};
function createBaseSmsSource() {
  return { name: "" };
}
var SmsSource = {
  fromJSON(object) {
    return { name: isSet(object.name) ? String(object.name) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.name !== "") {
      obj.name = message.name;
    }
    return obj;
  },
  create(base) {
    return SmsSource.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseSmsSource();
    message.name = object.name ?? "";
    return message;
  }
};
function isObject(value) {
  return typeof value === "object" && value !== null;
}
function isSet(value) {
  return value !== null && value !== void 0;
}

// src/__generated__/google/protobuf/timestamp.ts
function createBaseTimestamp() {
  return { seconds: 0, nanos: 0 };
}
var Timestamp = {
  fromJSON(object) {
    return {
      seconds: isSet2(object.seconds) ? Number(object.seconds) : 0,
      nanos: isSet2(object.nanos) ? Number(object.nanos) : 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.seconds !== 0) {
      obj.seconds = Math.round(message.seconds);
    }
    if (message.nanos !== 0) {
      obj.nanos = Math.round(message.nanos);
    }
    return obj;
  },
  create(base) {
    return Timestamp.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseTimestamp();
    message.seconds = object.seconds ?? 0;
    message.nanos = object.nanos ?? 0;
    return message;
  }
};
function isSet2(value) {
  return value !== null && value !== void 0;
}

// src/__generated__/protobuf/common/sell/offer_request.ts
function offerRequest_SourceFromJSON(object) {
  switch (object) {
    case 0:
    case "UNKNOWN_OFFER_SOURCE":
      return 0 /* UNKNOWN_OFFER_SOURCE */;
    case 1:
    case "REFRESH_ADMIN":
      return 1 /* REFRESH_ADMIN */;
    case 2:
    case "REFRESH_OFFER_DASHBOARD":
      return 2 /* REFRESH_OFFER_DASHBOARD */;
    case 3:
    case "REFRESH_BROKERAGE_OFFER_DASHBOARD":
      return 3 /* REFRESH_BROKERAGE_OFFER_DASHBOARD */;
    case 4:
    case "REFRESH_SELLER_FEEDBACK":
      return 4 /* REFRESH_SELLER_FEEDBACK */;
    case 5:
    case "REFRESH_REENGAGEMENT_EMAILS":
      return 5 /* REFRESH_REENGAGEMENT_EMAILS */;
    case 6:
    case "REFRESH_CORRECTED_SQ_FT_DATA":
      return 6 /* REFRESH_CORRECTED_SQ_FT_DATA */;
    case 7:
    case "INITIAL_LEAD_FLOW":
      return 7 /* INITIAL_LEAD_FLOW */;
    case 8:
    case "INCOMPLETE_INITIAL_LEAD_FLOW":
      return 8 /* INCOMPLETE_INITIAL_LEAD_FLOW */;
    case 9:
    case "OFFER_REENGAGEMENT_CAMPAIGN":
      return 9 /* OFFER_REENGAGEMENT_CAMPAIGN */;
    case 10:
    case "AUTOMATED_REENGAGEMENT_OFFER":
      return 10 /* AUTOMATED_REENGAGEMENT_OFFER */;
    case 11:
    case "MOBILE_APP":
      return 11 /* MOBILE_APP */;
    case 12:
    case "REIT_BULK_REQUEST":
      return 12 /* REIT_BULK_REQUEST */;
    case 13:
    case "OFFER_REVIEW":
      return 13 /* OFFER_REVIEW */;
    case 14:
    case "FLYWHEEL":
      return 14 /* FLYWHEEL */;
    case 15:
    case "EMAIL_PROSPECTING":
      return 15 /* EMAIL_PROSPECTING */;
    case 16:
    case "OUTBOUND_OFFERS_LISTED":
      return 16 /* OUTBOUND_OFFERS_LISTED */;
    case 17:
    case "REFRESH_PARTNER_REFERRAL":
      return 17 /* REFRESH_PARTNER_REFERRAL */;
    case 18:
    case "HOME_RESERVE":
      return 18 /* HOME_RESERVE */;
    case 19:
    case "HOMEPAGE_RETURN_EXPERIENCE":
      return 19 /* HOMEPAGE_RETURN_EXPERIENCE */;
    case 20:
    case "REFRESH_HOME_DETAILS":
      return 20 /* REFRESH_HOME_DETAILS */;
    case 21:
    case "REFRESH_FROM_ENG":
      return 21 /* REFRESH_FROM_ENG */;
    case 22:
    case "REFRESH_PARTNER_CTA":
      return 22 /* REFRESH_PARTNER_CTA */;
    case 23:
    case "RETURN_TO_HUB_AUTO_REFRESH":
      return 23 /* RETURN_TO_HUB_AUTO_REFRESH */;
    case 24:
    case "EXCLUSIVE_OFFERS":
      return 24 /* EXCLUSIVE_OFFERS */;
    case 25:
    case "REFRESH_PREVIOUSLY_DENIED":
      return 25 /* REFRESH_PREVIOUSLY_DENIED */;
    case 26:
    case "AUTOMATED_AGENT_REENGAGEMENT_OFFER":
      return 26 /* AUTOMATED_AGENT_REENGAGEMENT_OFFER */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function offerRequest_SourceToJSON(object) {
  switch (object) {
    case 0 /* UNKNOWN_OFFER_SOURCE */:
      return "UNKNOWN_OFFER_SOURCE";
    case 1 /* REFRESH_ADMIN */:
      return "REFRESH_ADMIN";
    case 2 /* REFRESH_OFFER_DASHBOARD */:
      return "REFRESH_OFFER_DASHBOARD";
    case 3 /* REFRESH_BROKERAGE_OFFER_DASHBOARD */:
      return "REFRESH_BROKERAGE_OFFER_DASHBOARD";
    case 4 /* REFRESH_SELLER_FEEDBACK */:
      return "REFRESH_SELLER_FEEDBACK";
    case 5 /* REFRESH_REENGAGEMENT_EMAILS */:
      return "REFRESH_REENGAGEMENT_EMAILS";
    case 6 /* REFRESH_CORRECTED_SQ_FT_DATA */:
      return "REFRESH_CORRECTED_SQ_FT_DATA";
    case 7 /* INITIAL_LEAD_FLOW */:
      return "INITIAL_LEAD_FLOW";
    case 8 /* INCOMPLETE_INITIAL_LEAD_FLOW */:
      return "INCOMPLETE_INITIAL_LEAD_FLOW";
    case 9 /* OFFER_REENGAGEMENT_CAMPAIGN */:
      return "OFFER_REENGAGEMENT_CAMPAIGN";
    case 10 /* AUTOMATED_REENGAGEMENT_OFFER */:
      return "AUTOMATED_REENGAGEMENT_OFFER";
    case 11 /* MOBILE_APP */:
      return "MOBILE_APP";
    case 12 /* REIT_BULK_REQUEST */:
      return "REIT_BULK_REQUEST";
    case 13 /* OFFER_REVIEW */:
      return "OFFER_REVIEW";
    case 14 /* FLYWHEEL */:
      return "FLYWHEEL";
    case 15 /* EMAIL_PROSPECTING */:
      return "EMAIL_PROSPECTING";
    case 16 /* OUTBOUND_OFFERS_LISTED */:
      return "OUTBOUND_OFFERS_LISTED";
    case 17 /* REFRESH_PARTNER_REFERRAL */:
      return "REFRESH_PARTNER_REFERRAL";
    case 18 /* HOME_RESERVE */:
      return "HOME_RESERVE";
    case 19 /* HOMEPAGE_RETURN_EXPERIENCE */:
      return "HOMEPAGE_RETURN_EXPERIENCE";
    case 20 /* REFRESH_HOME_DETAILS */:
      return "REFRESH_HOME_DETAILS";
    case 21 /* REFRESH_FROM_ENG */:
      return "REFRESH_FROM_ENG";
    case 22 /* REFRESH_PARTNER_CTA */:
      return "REFRESH_PARTNER_CTA";
    case 23 /* RETURN_TO_HUB_AUTO_REFRESH */:
      return "RETURN_TO_HUB_AUTO_REFRESH";
    case 24 /* EXCLUSIVE_OFFERS */:
      return "EXCLUSIVE_OFFERS";
    case 25 /* REFRESH_PREVIOUSLY_DENIED */:
      return "REFRESH_PREVIOUSLY_DENIED";
    case 26 /* AUTOMATED_AGENT_REENGAGEMENT_OFFER */:
      return "AUTOMATED_AGENT_REENGAGEMENT_OFFER";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}

// src/__generated__/protobuf/journey/data/contexts.ts
function createBaseCampaignContext() {
  return {
    utmSource: void 0,
    utmMedium: void 0,
    utmCampaign: void 0,
    utmTerm: void 0,
    utmContent: void 0,
    utmAdgid: void 0,
    utmNtwk: void 0,
    utmSubsite: void 0,
    utmCta: void 0,
    utmExternalid: void 0,
    utmGeo: void 0,
    utmCat: void 0
  };
}
var CampaignContext = {
  fromJSON(object) {
    return {
      utmSource: isSet3(object.utmSource) ? String(object.utmSource) : void 0,
      utmMedium: isSet3(object.utmMedium) ? String(object.utmMedium) : void 0,
      utmCampaign: isSet3(object.utmCampaign) ? String(object.utmCampaign) : void 0,
      utmTerm: isSet3(object.utmTerm) ? String(object.utmTerm) : void 0,
      utmContent: isSet3(object.utmContent) ? String(object.utmContent) : void 0,
      utmAdgid: isSet3(object.utmAdgid) ? String(object.utmAdgid) : void 0,
      utmNtwk: isSet3(object.utmNtwk) ? String(object.utmNtwk) : void 0,
      utmSubsite: isSet3(object.utmSubsite) ? String(object.utmSubsite) : void 0,
      utmCta: isSet3(object.utmCta) ? String(object.utmCta) : void 0,
      utmExternalid: isSet3(object.utmExternalid) ? String(object.utmExternalid) : void 0,
      utmGeo: isSet3(object.utmGeo) ? String(object.utmGeo) : void 0,
      utmCat: isSet3(object.utmCat) ? String(object.utmCat) : void 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.utmSource !== void 0) {
      obj.utmSource = message.utmSource;
    }
    if (message.utmMedium !== void 0) {
      obj.utmMedium = message.utmMedium;
    }
    if (message.utmCampaign !== void 0) {
      obj.utmCampaign = message.utmCampaign;
    }
    if (message.utmTerm !== void 0) {
      obj.utmTerm = message.utmTerm;
    }
    if (message.utmContent !== void 0) {
      obj.utmContent = message.utmContent;
    }
    if (message.utmAdgid !== void 0) {
      obj.utmAdgid = message.utmAdgid;
    }
    if (message.utmNtwk !== void 0) {
      obj.utmNtwk = message.utmNtwk;
    }
    if (message.utmSubsite !== void 0) {
      obj.utmSubsite = message.utmSubsite;
    }
    if (message.utmCta !== void 0) {
      obj.utmCta = message.utmCta;
    }
    if (message.utmExternalid !== void 0) {
      obj.utmExternalid = message.utmExternalid;
    }
    if (message.utmGeo !== void 0) {
      obj.utmGeo = message.utmGeo;
    }
    if (message.utmCat !== void 0) {
      obj.utmCat = message.utmCat;
    }
    return obj;
  },
  create(base) {
    return CampaignContext.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseCampaignContext();
    message.utmSource = object.utmSource ?? void 0;
    message.utmMedium = object.utmMedium ?? void 0;
    message.utmCampaign = object.utmCampaign ?? void 0;
    message.utmTerm = object.utmTerm ?? void 0;
    message.utmContent = object.utmContent ?? void 0;
    message.utmAdgid = object.utmAdgid ?? void 0;
    message.utmNtwk = object.utmNtwk ?? void 0;
    message.utmSubsite = object.utmSubsite ?? void 0;
    message.utmCta = object.utmCta ?? void 0;
    message.utmExternalid = object.utmExternalid ?? void 0;
    message.utmGeo = object.utmGeo ?? void 0;
    message.utmCat = object.utmCat ?? void 0;
    return message;
  }
};
function createBaseCustomerContext() {
  return {
    customerUuid: "",
    firstName: void 0,
    lastName: void 0,
    primaryEmail: void 0,
    primaryPhone: void 0
  };
}
var CustomerContext = {
  fromJSON(object) {
    return {
      customerUuid: isSet3(object.customerUuid) ? String(object.customerUuid) : "",
      firstName: isSet3(object.firstName) ? String(object.firstName) : void 0,
      lastName: isSet3(object.lastName) ? String(object.lastName) : void 0,
      primaryEmail: isSet3(object.primaryEmail) ? String(object.primaryEmail) : void 0,
      primaryPhone: isSet3(object.primaryPhone) ? String(object.primaryPhone) : void 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.customerUuid !== "") {
      obj.customerUuid = message.customerUuid;
    }
    if (message.firstName !== void 0) {
      obj.firstName = message.firstName;
    }
    if (message.lastName !== void 0) {
      obj.lastName = message.lastName;
    }
    if (message.primaryEmail !== void 0) {
      obj.primaryEmail = message.primaryEmail;
    }
    if (message.primaryPhone !== void 0) {
      obj.primaryPhone = message.primaryPhone;
    }
    return obj;
  },
  create(base) {
    return CustomerContext.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseCustomerContext();
    message.customerUuid = object.customerUuid ?? "";
    message.firstName = object.firstName ?? void 0;
    message.lastName = object.lastName ?? void 0;
    message.primaryEmail = object.primaryEmail ?? void 0;
    message.primaryPhone = object.primaryPhone ?? void 0;
    return message;
  }
};
function createBaseExclusiveOffersContext() {
  return {
    offerBlurPricing: void 0,
    offerRangeMax: void 0,
    offerRangeMin: void 0,
    potentialBuyerCount: void 0,
    allPhotoCount: void 0,
    internalPhotoCount: void 0,
    mlsPhotoCount: void 0
  };
}
var ExclusiveOffersContext = {
  fromJSON(object) {
    return {
      offerBlurPricing: isSet3(object.offerBlurPricing) ? Boolean(object.offerBlurPricing) : void 0,
      offerRangeMax: isSet3(object.offerRangeMax) ? Number(object.offerRangeMax) : void 0,
      offerRangeMin: isSet3(object.offerRangeMin) ? Number(object.offerRangeMin) : void 0,
      potentialBuyerCount: isSet3(object.potentialBuyerCount) ? Number(object.potentialBuyerCount) : void 0,
      allPhotoCount: isSet3(object.allPhotoCount) ? Number(object.allPhotoCount) : void 0,
      internalPhotoCount: isSet3(object.internalPhotoCount) ? Number(object.internalPhotoCount) : void 0,
      mlsPhotoCount: isSet3(object.mlsPhotoCount) ? Number(object.mlsPhotoCount) : void 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.offerBlurPricing !== void 0) {
      obj.offerBlurPricing = message.offerBlurPricing;
    }
    if (message.offerRangeMax !== void 0) {
      obj.offerRangeMax = message.offerRangeMax;
    }
    if (message.offerRangeMin !== void 0) {
      obj.offerRangeMin = message.offerRangeMin;
    }
    if (message.potentialBuyerCount !== void 0) {
      obj.potentialBuyerCount = message.potentialBuyerCount;
    }
    if (message.allPhotoCount !== void 0) {
      obj.allPhotoCount = message.allPhotoCount;
    }
    if (message.internalPhotoCount !== void 0) {
      obj.internalPhotoCount = message.internalPhotoCount;
    }
    if (message.mlsPhotoCount !== void 0) {
      obj.mlsPhotoCount = message.mlsPhotoCount;
    }
    return obj;
  },
  create(base) {
    return ExclusiveOffersContext.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseExclusiveOffersContext();
    message.offerBlurPricing = object.offerBlurPricing ?? void 0;
    message.offerRangeMax = object.offerRangeMax ?? void 0;
    message.offerRangeMin = object.offerRangeMin ?? void 0;
    message.potentialBuyerCount = object.potentialBuyerCount ?? void 0;
    message.allPhotoCount = object.allPhotoCount ?? void 0;
    message.internalPhotoCount = object.internalPhotoCount ?? void 0;
    message.mlsPhotoCount = object.mlsPhotoCount ?? void 0;
    return message;
  }
};
function createBaseLeadContext() {
  return { sellerInputUuid: void 0, sellerLeadUuid: void 0, sellerLeadMarket: void 0 };
}
var LeadContext = {
  fromJSON(object) {
    return {
      sellerInputUuid: isSet3(object.sellerInputUuid) ? String(object.sellerInputUuid) : void 0,
      sellerLeadUuid: isSet3(object.sellerLeadUuid) ? String(object.sellerLeadUuid) : void 0,
      sellerLeadMarket: isSet3(object.sellerLeadMarket) ? String(object.sellerLeadMarket) : void 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.sellerInputUuid !== void 0) {
      obj.sellerInputUuid = message.sellerInputUuid;
    }
    if (message.sellerLeadUuid !== void 0) {
      obj.sellerLeadUuid = message.sellerLeadUuid;
    }
    if (message.sellerLeadMarket !== void 0) {
      obj.sellerLeadMarket = message.sellerLeadMarket;
    }
    return obj;
  },
  create(base) {
    return LeadContext.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseLeadContext();
    message.sellerInputUuid = object.sellerInputUuid ?? void 0;
    message.sellerLeadUuid = object.sellerLeadUuid ?? void 0;
    message.sellerLeadMarket = object.sellerLeadMarket ?? void 0;
    return message;
  }
};
function createBaseSellDirectContext() {
  return {
    offerId: "",
    offerRequestSource: 0,
    offerHeadlinePrice: void 0,
    offerDenialReason: void 0,
    offerState: void 0,
    dwellingType: void 0
  };
}
var SellDirectContext = {
  fromJSON(object) {
    return {
      offerId: isSet3(object.offerId) ? String(object.offerId) : "",
      offerRequestSource: isSet3(object.offerRequestSource) ? offerRequest_SourceFromJSON(object.offerRequestSource) : 0,
      offerHeadlinePrice: isSet3(object.offerHeadlinePrice) ? Number(object.offerHeadlinePrice) : void 0,
      offerDenialReason: isSet3(object.offerDenialReason) ? String(object.offerDenialReason) : void 0,
      offerState: isSet3(object.offerState) ? String(object.offerState) : void 0,
      dwellingType: isSet3(object.dwellingType) ? String(object.dwellingType) : void 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.offerId !== "") {
      obj.offerId = message.offerId;
    }
    if (message.offerRequestSource !== 0) {
      obj.offerRequestSource = offerRequest_SourceToJSON(message.offerRequestSource);
    }
    if (message.offerHeadlinePrice !== void 0) {
      obj.offerHeadlinePrice = message.offerHeadlinePrice;
    }
    if (message.offerDenialReason !== void 0) {
      obj.offerDenialReason = message.offerDenialReason;
    }
    if (message.offerState !== void 0) {
      obj.offerState = message.offerState;
    }
    if (message.dwellingType !== void 0) {
      obj.dwellingType = message.dwellingType;
    }
    return obj;
  },
  create(base) {
    return SellDirectContext.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseSellDirectContext();
    message.offerId = object.offerId ?? "";
    message.offerRequestSource = object.offerRequestSource ?? 0;
    message.offerHeadlinePrice = object.offerHeadlinePrice ?? void 0;
    message.offerDenialReason = object.offerDenialReason ?? void 0;
    message.offerState = object.offerState ?? void 0;
    message.dwellingType = object.dwellingType ?? void 0;
    return message;
  }
};
function createBaseAdClickContext() {
  return { fbclid: void 0, ttclid: void 0, gclid: void 0 };
}
var AdClickContext = {
  fromJSON(object) {
    return {
      fbclid: isSet3(object.fbclid) ? String(object.fbclid) : void 0,
      ttclid: isSet3(object.ttclid) ? String(object.ttclid) : void 0,
      gclid: isSet3(object.gclid) ? String(object.gclid) : void 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.fbclid !== void 0) {
      obj.fbclid = message.fbclid;
    }
    if (message.ttclid !== void 0) {
      obj.ttclid = message.ttclid;
    }
    if (message.gclid !== void 0) {
      obj.gclid = message.gclid;
    }
    return obj;
  },
  create(base) {
    return AdClickContext.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseAdClickContext();
    message.fbclid = object.fbclid ?? void 0;
    message.ttclid = object.ttclid ?? void 0;
    message.gclid = object.gclid ?? void 0;
    return message;
  }
};
function isSet3(value) {
  return value !== null && value !== void 0;
}

// src/__generated__/protobuf/sell/common/common.ts
function productFromJSON(object) {
  switch (object) {
    case 0:
    case "UNKNOWN_PRODUCT":
      return 0 /* UNKNOWN_PRODUCT */;
    case 1:
    case "OPENDOOR_DIRECT":
      return 1 /* OPENDOOR_DIRECT */;
    case 2:
    case "AGENT_REFERRAL":
      return 2 /* AGENT_REFERRAL */;
    case 3:
    case "REIT":
      return 3 /* REIT */;
    case 4:
    case "LIST_WITH_OPENDOOR":
      return 4 /* LIST_WITH_OPENDOOR */;
    case 5:
    case "SELL_WITH_UPSIDE":
      return 5 /* SELL_WITH_UPSIDE */;
    case 6:
    case "BUY_WITH_OPENDOOR":
      return 6 /* BUY_WITH_OPENDOOR */;
    case 7:
    case "OPENDOOR_BACKED_OFFERS":
      return 7 /* OPENDOOR_BACKED_OFFERS */;
    case 8:
    case "SELL_WITH_EXCLUSIVES":
      return 8 /* SELL_WITH_EXCLUSIVES */;
    case 9:
    case "LIST_WITH_CERTAINTY":
      return 9 /* LIST_WITH_CERTAINTY */;
    case 10:
    case "EXTERNAL_LISTING_AGENT":
      return 10 /* EXTERNAL_LISTING_AGENT */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function productToJSON(object) {
  switch (object) {
    case 0 /* UNKNOWN_PRODUCT */:
      return "UNKNOWN_PRODUCT";
    case 1 /* OPENDOOR_DIRECT */:
      return "OPENDOOR_DIRECT";
    case 2 /* AGENT_REFERRAL */:
      return "AGENT_REFERRAL";
    case 3 /* REIT */:
      return "REIT";
    case 4 /* LIST_WITH_OPENDOOR */:
      return "LIST_WITH_OPENDOOR";
    case 5 /* SELL_WITH_UPSIDE */:
      return "SELL_WITH_UPSIDE";
    case 6 /* BUY_WITH_OPENDOOR */:
      return "BUY_WITH_OPENDOOR";
    case 7 /* OPENDOOR_BACKED_OFFERS */:
      return "OPENDOOR_BACKED_OFFERS";
    case 8 /* SELL_WITH_EXCLUSIVES */:
      return "SELL_WITH_EXCLUSIVES";
    case 9 /* LIST_WITH_CERTAINTY */:
      return "LIST_WITH_CERTAINTY";
    case 10 /* EXTERNAL_LISTING_AGENT */:
      return "EXTERNAL_LISTING_AGENT";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}

// src/__generated__/protobuf/web/data/sell_direct/sell_direct_event_types.ts
function sellDirectEventTypeFromJSON(object) {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return 0 /* UNKNOWN */;
    case 1:
    case "PRELIM_OFFER_SENT":
      return 1 /* PRELIM_OFFER_SENT */;
    case 2:
    case "FINAL_OFFER_SENT":
      return 2 /* FINAL_OFFER_SENT */;
    case 3:
    case "FINAL_OFFER_VIEWED":
      return 3 /* FINAL_OFFER_VIEWED */;
    case 4:
    case "OFFER_CONTINGENCY_RELEASED":
      return 4 /* OFFER_CONTINGENCY_RELEASED */;
    case 5:
    case "OFFER_CLOSED":
      return 5 /* OFFER_CLOSED */;
    case 6:
    case "OFFER_EXPIRED":
      return 6 /* OFFER_EXPIRED */;
    case 7:
    case "OFFER_WITHDRAWN":
      return 7 /* OFFER_WITHDRAWN */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function sellDirectEventTypeToJSON(object) {
  switch (object) {
    case 0 /* UNKNOWN */:
      return "UNKNOWN";
    case 1 /* PRELIM_OFFER_SENT */:
      return "PRELIM_OFFER_SENT";
    case 2 /* FINAL_OFFER_SENT */:
      return "FINAL_OFFER_SENT";
    case 3 /* FINAL_OFFER_VIEWED */:
      return "FINAL_OFFER_VIEWED";
    case 4 /* OFFER_CONTINGENCY_RELEASED */:
      return "OFFER_CONTINGENCY_RELEASED";
    case 5 /* OFFER_CLOSED */:
      return "OFFER_CLOSED";
    case 6 /* OFFER_EXPIRED */:
      return "OFFER_EXPIRED";
    case 7 /* OFFER_WITHDRAWN */:
      return "OFFER_WITHDRAWN";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}

// src/__generated__/protobuf/journey/data/conversion_events.ts
function sellerInputEvent_EventTypeFromJSON(object) {
  switch (object) {
    case 0:
    case "UNSPECIFIED":
      return 0 /* UNSPECIFIED */;
    case 1:
    case "CREATED":
      return 1 /* CREATED */;
    case 2:
    case "NEW_ANSWERS":
      return 2 /* NEW_ANSWERS */;
    case 3:
    case "PRODUCT_MATCHED":
      return 3 /* PRODUCT_MATCHED */;
    case 4:
    case "PRODUCT_QUALIFIED":
      return 4 /* PRODUCT_QUALIFIED */;
    case 5:
    case "PRODUCT_DENIED":
      return 5 /* PRODUCT_DENIED */;
    case 6:
    case "PRODUCT_UNDENIED":
      return 6 /* PRODUCT_UNDENIED */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function sellerInputEvent_EventTypeToJSON(object) {
  switch (object) {
    case 0 /* UNSPECIFIED */:
      return "UNSPECIFIED";
    case 1 /* CREATED */:
      return "CREATED";
    case 2 /* NEW_ANSWERS */:
      return "NEW_ANSWERS";
    case 3 /* PRODUCT_MATCHED */:
      return "PRODUCT_MATCHED";
    case 4 /* PRODUCT_QUALIFIED */:
      return "PRODUCT_QUALIFIED";
    case 5 /* PRODUCT_DENIED */:
      return "PRODUCT_DENIED";
    case 6 /* PRODUCT_UNDENIED */:
      return "PRODUCT_UNDENIED";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}
function createBaseSellDirectConversionEvent() {
  return { conversionEvent: 0 };
}
var SellDirectConversionEvent = {
  fromJSON(object) {
    return { conversionEvent: isSet4(object.conversionEvent) ? sellDirectEventTypeFromJSON(object.conversionEvent) : 0 };
  },
  toJSON(message) {
    const obj = {};
    if (message.conversionEvent !== 0) {
      obj.conversionEvent = sellDirectEventTypeToJSON(message.conversionEvent);
    }
    return obj;
  },
  create(base) {
    return SellDirectConversionEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseSellDirectConversionEvent();
    message.conversionEvent = object.conversionEvent ?? 0;
    return message;
  }
};
function createBaseSellerInputEvent() {
  return { eventType: 0, answersJson: void 0 };
}
var SellerInputEvent = {
  fromJSON(object) {
    return {
      eventType: isSet4(object.eventType) ? sellerInputEvent_EventTypeFromJSON(object.eventType) : 0,
      answersJson: isSet4(object.answersJson) ? String(object.answersJson) : void 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.eventType !== 0) {
      obj.eventType = sellerInputEvent_EventTypeToJSON(message.eventType);
    }
    if (message.answersJson !== void 0) {
      obj.answersJson = message.answersJson;
    }
    return obj;
  },
  create(base) {
    return SellerInputEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseSellerInputEvent();
    message.eventType = object.eventType ?? 0;
    message.answersJson = object.answersJson ?? void 0;
    return message;
  }
};
function createBaseLeadQualifiedEvent() {
  return { product: 0 };
}
var LeadQualifiedEvent = {
  fromJSON(object) {
    return { product: isSet4(object.product) ? productFromJSON(object.product) : 0 };
  },
  toJSON(message) {
    const obj = {};
    if (message.product !== 0) {
      obj.product = productToJSON(message.product);
    }
    return obj;
  },
  create(base) {
    return LeadQualifiedEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseLeadQualifiedEvent();
    message.product = object.product ?? 0;
    return message;
  }
};
function isSet4(value) {
  return value !== null && value !== void 0;
}

// src/__generated__/protobuf/journey/data/events.ts
function buyerEvent_EventTypeFromJSON(object) {
  switch (object) {
    case 0:
    case "UNSPECIFIED":
      return 0 /* UNSPECIFIED */;
    case 1:
    case "TOUR_SCHEDULE_START":
      return 1 /* TOUR_SCHEDULE_START */;
    case 2:
    case "TOUR_SCHEDULED":
      return 2 /* TOUR_SCHEDULED */;
    case 3:
    case "TOUR_START":
      return 3 /* TOUR_START */;
    case 4:
    case "TOUR_COMPLETE":
      return 4 /* TOUR_COMPLETE */;
    case 5:
    case "OFFER_START":
      return 5 /* OFFER_START */;
    case 6:
    case "OFFER_SUBMITTED":
      return 6 /* OFFER_SUBMITTED */;
    case 7:
    case "OFFER_CANCELED":
      return 7 /* OFFER_CANCELED */;
    case 8:
    case "OFFER_REJECTED":
      return 8 /* OFFER_REJECTED */;
    case 9:
    case "OFFER_ACCEPTED":
      return 9 /* OFFER_ACCEPTED */;
    case 10:
    case "OFFER_CONTRACT_START":
      return 10 /* OFFER_CONTRACT_START */;
    case 11:
    case "OFFER_CONTRACT_COMPLETE":
      return 11 /* OFFER_CONTRACT_COMPLETE */;
    case 12:
    case "TOUR_CONFIRMED":
      return 12 /* TOUR_CONFIRMED */;
    case 13:
    case "FEED_SETUP_COMPLETE":
      return 13 /* FEED_SETUP_COMPLETE */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function buyerEvent_EventTypeToJSON(object) {
  switch (object) {
    case 0 /* UNSPECIFIED */:
      return "UNSPECIFIED";
    case 1 /* TOUR_SCHEDULE_START */:
      return "TOUR_SCHEDULE_START";
    case 2 /* TOUR_SCHEDULED */:
      return "TOUR_SCHEDULED";
    case 3 /* TOUR_START */:
      return "TOUR_START";
    case 4 /* TOUR_COMPLETE */:
      return "TOUR_COMPLETE";
    case 5 /* OFFER_START */:
      return "OFFER_START";
    case 6 /* OFFER_SUBMITTED */:
      return "OFFER_SUBMITTED";
    case 7 /* OFFER_CANCELED */:
      return "OFFER_CANCELED";
    case 8 /* OFFER_REJECTED */:
      return "OFFER_REJECTED";
    case 9 /* OFFER_ACCEPTED */:
      return "OFFER_ACCEPTED";
    case 10 /* OFFER_CONTRACT_START */:
      return "OFFER_CONTRACT_START";
    case 11 /* OFFER_CONTRACT_COMPLETE */:
      return "OFFER_CONTRACT_COMPLETE";
    case 12 /* TOUR_CONFIRMED */:
      return "TOUR_CONFIRMED";
    case 13 /* FEED_SETUP_COMPLETE */:
      return "FEED_SETUP_COMPLETE";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}
function smsEvent_EventTypeFromJSON(object) {
  switch (object) {
    case 0:
    case "UNSPECIFIED":
      return 0 /* UNSPECIFIED */;
    case 1:
    case "TRIGGER":
      return 1 /* TRIGGER */;
    case 2:
    case "ADDRESS_PARSE_SUCCESSFUL":
      return 2 /* ADDRESS_PARSE_SUCCESSFUL */;
    case 3:
    case "ADDRESS_PARSE_FAIL":
      return 3 /* ADDRESS_PARSE_FAIL */;
    case 4:
    case "BALLPARK_ESTIMATE_SUCCESS":
      return 4 /* BALLPARK_ESTIMATE_SUCCESS */;
    case 5:
    case "BALLPARK_ESTIMATE_FAIL":
      return 5 /* BALLPARK_ESTIMATE_FAIL */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function smsEvent_EventTypeToJSON(object) {
  switch (object) {
    case 0 /* UNSPECIFIED */:
      return "UNSPECIFIED";
    case 1 /* TRIGGER */:
      return "TRIGGER";
    case 2 /* ADDRESS_PARSE_SUCCESSFUL */:
      return "ADDRESS_PARSE_SUCCESSFUL";
    case 3 /* ADDRESS_PARSE_FAIL */:
      return "ADDRESS_PARSE_FAIL";
    case 4 /* BALLPARK_ESTIMATE_SUCCESS */:
      return "BALLPARK_ESTIMATE_SUCCESS";
    case 5 /* BALLPARK_ESTIMATE_FAIL */:
      return "BALLPARK_ESTIMATE_FAIL";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}
function authenticationEvent_EventTypeFromJSON(object) {
  switch (object) {
    case 0:
    case "EVENT_UNSPECIFIED":
      return 0 /* EVENT_UNSPECIFIED */;
    case 1:
    case "LOGIN_SUCCESS":
      return 1 /* LOGIN_SUCCESS */;
    case 2:
    case "LOGIN_ERROR":
      return 2 /* LOGIN_ERROR */;
    case 3:
    case "REGISTRATION_SUCCESS":
      return 3 /* REGISTRATION_SUCCESS */;
    case 4:
    case "REGISTRATION_ERROR":
      return 4 /* REGISTRATION_ERROR */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function authenticationEvent_EventTypeToJSON(object) {
  switch (object) {
    case 0 /* EVENT_UNSPECIFIED */:
      return "EVENT_UNSPECIFIED";
    case 1 /* LOGIN_SUCCESS */:
      return "LOGIN_SUCCESS";
    case 2 /* LOGIN_ERROR */:
      return "LOGIN_ERROR";
    case 3 /* REGISTRATION_SUCCESS */:
      return "REGISTRATION_SUCCESS";
    case 4 /* REGISTRATION_ERROR */:
      return "REGISTRATION_ERROR";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}
function authenticationEvent_AuthMethodFromJSON(object) {
  switch (object) {
    case 0:
    case "METHOD_UNSPECIFIED":
      return 0 /* METHOD_UNSPECIFIED */;
    case 1:
    case "EMAIL_PASSWORD":
      return 1 /* EMAIL_PASSWORD */;
    case 2:
    case "LITE":
      return 2 /* LITE */;
    case 3:
    case "GOOGLE":
      return 3 /* GOOGLE */;
    case 4:
    case "FACEBOOK":
      return 4 /* FACEBOOK */;
    case 5:
    case "SESSION_ALREADY_EXISTS":
      return 5 /* SESSION_ALREADY_EXISTS */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function authenticationEvent_AuthMethodToJSON(object) {
  switch (object) {
    case 0 /* METHOD_UNSPECIFIED */:
      return "METHOD_UNSPECIFIED";
    case 1 /* EMAIL_PASSWORD */:
      return "EMAIL_PASSWORD";
    case 2 /* LITE */:
      return "LITE";
    case 3 /* GOOGLE */:
      return "GOOGLE";
    case 4 /* FACEBOOK */:
      return "FACEBOOK";
    case 5 /* SESSION_ALREADY_EXISTS */:
      return "SESSION_ALREADY_EXISTS";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}
function createBaseAccordionClickEvent() {
  return { targetName: "", accordionName: "" };
}
var AccordionClickEvent = {
  fromJSON(object) {
    return {
      targetName: isSet5(object.targetName) ? String(object.targetName) : "",
      accordionName: isSet5(object.accordionName) ? String(object.accordionName) : ""
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    if (message.accordionName !== "") {
      obj.accordionName = message.accordionName;
    }
    return obj;
  },
  create(base) {
    return AccordionClickEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseAccordionClickEvent();
    message.targetName = object.targetName ?? "";
    message.accordionName = object.accordionName ?? "";
    return message;
  }
};
function createBaseAccordionHoverEvent() {
  return { targetName: "", accordionName: "" };
}
var AccordionHoverEvent = {
  fromJSON(object) {
    return {
      targetName: isSet5(object.targetName) ? String(object.targetName) : "",
      accordionName: isSet5(object.accordionName) ? String(object.accordionName) : ""
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    if (message.accordionName !== "") {
      obj.accordionName = message.accordionName;
    }
    return obj;
  },
  create(base) {
    return AccordionHoverEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseAccordionHoverEvent();
    message.targetName = object.targetName ?? "";
    message.accordionName = object.accordionName ?? "";
    return message;
  }
};
function createBaseAccordionViewEvent() {
  return { targetName: "" };
}
var AccordionViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return AccordionViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseAccordionViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseBoxViewEvent() {
  return { targetName: "" };
}
var BoxViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return BoxViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseBoxViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseButtonClickEvent() {
  return { targetName: "" };
}
var ButtonClickEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return ButtonClickEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseButtonClickEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseButtonHoverEvent() {
  return { targetName: "" };
}
var ButtonHoverEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return ButtonHoverEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseButtonHoverEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseButtonViewEvent() {
  return { targetName: "" };
}
var ButtonViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return ButtonViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseButtonViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseBuyerEvent() {
  return { eventType: 0 };
}
var BuyerEvent = {
  fromJSON(object) {
    return { eventType: isSet5(object.eventType) ? buyerEvent_EventTypeFromJSON(object.eventType) : 0 };
  },
  toJSON(message) {
    const obj = {};
    if (message.eventType !== 0) {
      obj.eventType = buyerEvent_EventTypeToJSON(message.eventType);
    }
    return obj;
  },
  create(base) {
    return BuyerEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseBuyerEvent();
    message.eventType = object.eventType ?? 0;
    return message;
  }
};
function createBaseCardViewEvent() {
  return { targetName: "" };
}
var CardViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return CardViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseCardViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseCheckboxClickEvent() {
  return { targetName: "", optionName: "" };
}
var CheckboxClickEvent = {
  fromJSON(object) {
    return {
      targetName: isSet5(object.targetName) ? String(object.targetName) : "",
      optionName: isSet5(object.optionName) ? String(object.optionName) : ""
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    if (message.optionName !== "") {
      obj.optionName = message.optionName;
    }
    return obj;
  },
  create(base) {
    return CheckboxClickEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseCheckboxClickEvent();
    message.targetName = object.targetName ?? "";
    message.optionName = object.optionName ?? "";
    return message;
  }
};
function createBaseCheckboxHoverEvent() {
  return { targetName: "", optionName: "" };
}
var CheckboxHoverEvent = {
  fromJSON(object) {
    return {
      targetName: isSet5(object.targetName) ? String(object.targetName) : "",
      optionName: isSet5(object.optionName) ? String(object.optionName) : ""
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    if (message.optionName !== "") {
      obj.optionName = message.optionName;
    }
    return obj;
  },
  create(base) {
    return CheckboxHoverEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseCheckboxHoverEvent();
    message.targetName = object.targetName ?? "";
    message.optionName = object.optionName ?? "";
    return message;
  }
};
function createBaseCheckboxViewEvent() {
  return { targetName: "" };
}
var CheckboxViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return CheckboxViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseCheckboxViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseExternalLeadGeneratedEvent() {
  return {
    leadSource: "",
    firstName: void 0,
    middleName: void 0,
    lastName: void 0,
    email: void 0,
    addressLine1: void 0,
    addressLine2: void 0,
    city: void 0,
    state: void 0,
    postalCode: void 0,
    leadProperties: {}
  };
}
var ExternalLeadGeneratedEvent = {
  fromJSON(object) {
    return {
      leadSource: isSet5(object.leadSource) ? String(object.leadSource) : "",
      firstName: isSet5(object.firstName) ? String(object.firstName) : void 0,
      middleName: isSet5(object.middleName) ? String(object.middleName) : void 0,
      lastName: isSet5(object.lastName) ? String(object.lastName) : void 0,
      email: isSet5(object.email) ? String(object.email) : void 0,
      addressLine1: isSet5(object.addressLine1) ? String(object.addressLine1) : void 0,
      addressLine2: isSet5(object.addressLine2) ? String(object.addressLine2) : void 0,
      city: isSet5(object.city) ? String(object.city) : void 0,
      state: isSet5(object.state) ? String(object.state) : void 0,
      postalCode: isSet5(object.postalCode) ? String(object.postalCode) : void 0,
      leadProperties: isObject2(object.leadProperties) ? Object.entries(object.leadProperties).reduce((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {}) : {}
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.leadSource !== "") {
      obj.leadSource = message.leadSource;
    }
    if (message.firstName !== void 0) {
      obj.firstName = message.firstName;
    }
    if (message.middleName !== void 0) {
      obj.middleName = message.middleName;
    }
    if (message.lastName !== void 0) {
      obj.lastName = message.lastName;
    }
    if (message.email !== void 0) {
      obj.email = message.email;
    }
    if (message.addressLine1 !== void 0) {
      obj.addressLine1 = message.addressLine1;
    }
    if (message.addressLine2 !== void 0) {
      obj.addressLine2 = message.addressLine2;
    }
    if (message.city !== void 0) {
      obj.city = message.city;
    }
    if (message.state !== void 0) {
      obj.state = message.state;
    }
    if (message.postalCode !== void 0) {
      obj.postalCode = message.postalCode;
    }
    if (message.leadProperties) {
      const entries = Object.entries(message.leadProperties);
      if (entries.length > 0) {
        obj.leadProperties = {};
        entries.forEach(([k, v]) => {
          obj.leadProperties[k] = v;
        });
      }
    }
    return obj;
  },
  create(base) {
    return ExternalLeadGeneratedEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseExternalLeadGeneratedEvent();
    message.leadSource = object.leadSource ?? "";
    message.firstName = object.firstName ?? void 0;
    message.middleName = object.middleName ?? void 0;
    message.lastName = object.lastName ?? void 0;
    message.email = object.email ?? void 0;
    message.addressLine1 = object.addressLine1 ?? void 0;
    message.addressLine2 = object.addressLine2 ?? void 0;
    message.city = object.city ?? void 0;
    message.state = object.state ?? void 0;
    message.postalCode = object.postalCode ?? void 0;
    message.leadProperties = Object.entries(object.leadProperties ?? {}).reduce(
      (acc, [key, value]) => {
        if (value !== void 0) {
          acc[key] = String(value);
        }
        return acc;
      },
      {}
    );
    return message;
  }
};
function createBaseImageViewEvent() {
  return { targetName: "" };
}
var ImageViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return ImageViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseImageViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseInputBlurEvent() {
  return { targetName: "" };
}
var InputBlurEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return InputBlurEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseInputBlurEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseInputFocusEvent() {
  return { targetName: "" };
}
var InputFocusEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return InputFocusEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseInputFocusEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseInputHoverEvent() {
  return { targetName: "" };
}
var InputHoverEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return InputHoverEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseInputHoverEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseInputViewEvent() {
  return { targetName: "" };
}
var InputViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return InputViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseInputViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseLinkClickEvent() {
  return { targetName: "" };
}
var LinkClickEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return LinkClickEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseLinkClickEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseLinkHoverEvent() {
  return { targetName: "" };
}
var LinkHoverEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return LinkHoverEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseLinkHoverEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseLinkViewEvent() {
  return { targetName: "" };
}
var LinkViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return LinkViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseLinkViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseOutreachEvent() {
  return { eventType: "", bucket: "" };
}
var OutreachEvent = {
  fromJSON(object) {
    return {
      eventType: isSet5(object.eventType) ? String(object.eventType) : "",
      bucket: isSet5(object.bucket) ? String(object.bucket) : ""
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.eventType !== "") {
      obj.eventType = message.eventType;
    }
    if (message.bucket !== "") {
      obj.bucket = message.bucket;
    }
    return obj;
  },
  create(base) {
    return OutreachEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseOutreachEvent();
    message.eventType = object.eventType ?? "";
    message.bucket = object.bucket ?? "";
    return message;
  }
};
function createBasePageViewEvent() {
  return {};
}
var PageViewEvent = {
  fromJSON(_) {
    return {};
  },
  toJSON(_) {
    const obj = {};
    return obj;
  },
  create(base) {
    return PageViewEvent.fromPartial(base ?? {});
  },
  fromPartial(_) {
    const message = createBasePageViewEvent();
    return message;
  }
};
function createBaseRadioClickEvent() {
  return { targetName: "", optionName: "" };
}
var RadioClickEvent = {
  fromJSON(object) {
    return {
      targetName: isSet5(object.targetName) ? String(object.targetName) : "",
      optionName: isSet5(object.optionName) ? String(object.optionName) : ""
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    if (message.optionName !== "") {
      obj.optionName = message.optionName;
    }
    return obj;
  },
  create(base) {
    return RadioClickEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseRadioClickEvent();
    message.targetName = object.targetName ?? "";
    message.optionName = object.optionName ?? "";
    return message;
  }
};
function createBaseRadioHoverEvent() {
  return { targetName: "", optionName: "" };
}
var RadioHoverEvent = {
  fromJSON(object) {
    return {
      targetName: isSet5(object.targetName) ? String(object.targetName) : "",
      optionName: isSet5(object.optionName) ? String(object.optionName) : ""
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    if (message.optionName !== "") {
      obj.optionName = message.optionName;
    }
    return obj;
  },
  create(base) {
    return RadioHoverEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseRadioHoverEvent();
    message.targetName = object.targetName ?? "";
    message.optionName = object.optionName ?? "";
    return message;
  }
};
function createBaseRadioViewEvent() {
  return { targetName: "" };
}
var RadioViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return RadioViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseRadioViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseSmsEvent() {
  return { eventType: 0, phoneNumber: "", address: "", ballparkEstimate: "" };
}
var SmsEvent = {
  fromJSON(object) {
    return {
      eventType: isSet5(object.eventType) ? smsEvent_EventTypeFromJSON(object.eventType) : 0,
      phoneNumber: isSet5(object.phoneNumber) ? String(object.phoneNumber) : "",
      address: isSet5(object.address) ? String(object.address) : "",
      ballparkEstimate: isSet5(object.ballparkEstimate) ? String(object.ballparkEstimate) : ""
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.eventType !== 0) {
      obj.eventType = smsEvent_EventTypeToJSON(message.eventType);
    }
    if (message.phoneNumber !== "") {
      obj.phoneNumber = message.phoneNumber;
    }
    if (message.address !== "") {
      obj.address = message.address;
    }
    if (message.ballparkEstimate !== "") {
      obj.ballparkEstimate = message.ballparkEstimate;
    }
    return obj;
  },
  create(base) {
    return SmsEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseSmsEvent();
    message.eventType = object.eventType ?? 0;
    message.phoneNumber = object.phoneNumber ?? "";
    message.address = object.address ?? "";
    message.ballparkEstimate = object.ballparkEstimate ?? "";
    return message;
  }
};
function createBaseTextViewEvent() {
  return { targetName: "" };
}
var TextViewEvent = {
  fromJSON(object) {
    return { targetName: isSet5(object.targetName) ? String(object.targetName) : "" };
  },
  toJSON(message) {
    const obj = {};
    if (message.targetName !== "") {
      obj.targetName = message.targetName;
    }
    return obj;
  },
  create(base) {
    return TextViewEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseTextViewEvent();
    message.targetName = object.targetName ?? "";
    return message;
  }
};
function createBaseAuthenticationEvent() {
  return { eventType: 0, authMethod: 0 };
}
var AuthenticationEvent = {
  fromJSON(object) {
    return {
      eventType: isSet5(object.eventType) ? authenticationEvent_EventTypeFromJSON(object.eventType) : 0,
      authMethod: isSet5(object.authMethod) ? authenticationEvent_AuthMethodFromJSON(object.authMethod) : 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.eventType !== 0) {
      obj.eventType = authenticationEvent_EventTypeToJSON(message.eventType);
    }
    if (message.authMethod !== 0) {
      obj.authMethod = authenticationEvent_AuthMethodToJSON(message.authMethod);
    }
    return obj;
  },
  create(base) {
    return AuthenticationEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBaseAuthenticationEvent();
    message.eventType = object.eventType ?? 0;
    message.authMethod = object.authMethod ?? 0;
    return message;
  }
};
function isObject2(value) {
  return typeof value === "object" && value !== null;
}
function isSet5(value) {
  return value !== null && value !== void 0;
}

// src/__generated__/protobuf/journey/data/partner_events.ts
function partnerEvent_EventTypeFromJSON(object) {
  switch (object) {
    case 0:
    case "UNSPECIFIED":
      return 0 /* UNSPECIFIED */;
    case 1:
    case "REFERRAL_OFFER_REQUESTED":
      return 1 /* REFERRAL_OFFER_REQUESTED */;
    case 2:
    case "REFERRAL_HANDOVER":
      return 2 /* REFERRAL_HANDOVER */;
    case -1:
    case "UNRECOGNIZED":
    default:
      return -1 /* UNRECOGNIZED */;
  }
}
function partnerEvent_EventTypeToJSON(object) {
  switch (object) {
    case 0 /* UNSPECIFIED */:
      return "UNSPECIFIED";
    case 1 /* REFERRAL_OFFER_REQUESTED */:
      return "REFERRAL_OFFER_REQUESTED";
    case 2 /* REFERRAL_HANDOVER */:
      return "REFERRAL_HANDOVER";
    case -1 /* UNRECOGNIZED */:
    default:
      return "UNRECOGNIZED";
  }
}
function createBasePartnerEvent() {
  return { partnerIdentifier: "", eventType: 0 };
}
var PartnerEvent = {
  fromJSON(object) {
    return {
      partnerIdentifier: isSet6(object.partnerIdentifier) ? String(object.partnerIdentifier) : "",
      eventType: isSet6(object.eventType) ? partnerEvent_EventTypeFromJSON(object.eventType) : 0
    };
  },
  toJSON(message) {
    const obj = {};
    if (message.partnerIdentifier !== "") {
      obj.partnerIdentifier = message.partnerIdentifier;
    }
    if (message.eventType !== 0) {
      obj.eventType = partnerEvent_EventTypeToJSON(message.eventType);
    }
    return obj;
  },
  create(base) {
    return PartnerEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    const message = createBasePartnerEvent();
    message.partnerIdentifier = object.partnerIdentifier ?? "";
    message.eventType = object.eventType ?? 0;
    return message;
  }
};
function isSet6(value) {
  return value !== null && value !== void 0;
}

// src/__generated__/protobuf/journey/data/user_event.ts
function createBaseUserEvent() {
  return {
    uuid: "",
    timestamp: void 0,
    anonymousId: void 0,
    humanId: void 0,
    source: void 0,
    event: void 0,
    customerContext: void 0,
    campaignContext: void 0,
    exclusiveOffersContext: void 0,
    leadContext: void 0,
    sellDirectContext: void 0,
    adClickContext: void 0
  };
}
var UserEvent = {
  fromJSON(object) {
    return {
      uuid: isSet7(object.uuid) ? String(object.uuid) : "",
      timestamp: isSet7(object.timestamp) ? fromJsonTimestamp(object.timestamp) : void 0,
      anonymousId: isSet7(object.anonymousId) ? String(object.anonymousId) : void 0,
      humanId: isSet7(object.humanId) ? String(object.humanId) : void 0,
      source: isSet7(object.websiteSource) ? { $case: "websiteSource", websiteSource: WebsiteSource.fromJSON(object.websiteSource) } : isSet7(object.mobileSource) ? { $case: "mobileSource", mobileSource: MobileSource.fromJSON(object.mobileSource) } : isSet7(object.backendSource) ? { $case: "backendSource", backendSource: BackendSource.fromJSON(object.backendSource) } : isSet7(object.smsSource) ? { $case: "smsSource", smsSource: SmsSource.fromJSON(object.smsSource) } : void 0,
      event: isSet7(object.buttonClickEvent) ? { $case: "buttonClickEvent", buttonClickEvent: ButtonClickEvent.fromJSON(object.buttonClickEvent) } : isSet7(object.buttonViewEvent) ? { $case: "buttonViewEvent", buttonViewEvent: ButtonViewEvent.fromJSON(object.buttonViewEvent) } : isSet7(object.buttonHoverEvent) ? { $case: "buttonHoverEvent", buttonHoverEvent: ButtonHoverEvent.fromJSON(object.buttonHoverEvent) } : isSet7(object.linkClickEvent) ? { $case: "linkClickEvent", linkClickEvent: LinkClickEvent.fromJSON(object.linkClickEvent) } : isSet7(object.linkViewEvent) ? { $case: "linkViewEvent", linkViewEvent: LinkViewEvent.fromJSON(object.linkViewEvent) } : isSet7(object.linkHoverEvent) ? { $case: "linkHoverEvent", linkHoverEvent: LinkHoverEvent.fromJSON(object.linkHoverEvent) } : isSet7(object.checkboxClickEvent) ? { $case: "checkboxClickEvent", checkboxClickEvent: CheckboxClickEvent.fromJSON(object.checkboxClickEvent) } : isSet7(object.checkboxViewEvent) ? { $case: "checkboxViewEvent", checkboxViewEvent: CheckboxViewEvent.fromJSON(object.checkboxViewEvent) } : isSet7(object.checkboxHoverEvent) ? { $case: "checkboxHoverEvent", checkboxHoverEvent: CheckboxHoverEvent.fromJSON(object.checkboxHoverEvent) } : isSet7(object.radioClickEvent) ? { $case: "radioClickEvent", radioClickEvent: RadioClickEvent.fromJSON(object.radioClickEvent) } : isSet7(object.radioViewEvent) ? { $case: "radioViewEvent", radioViewEvent: RadioViewEvent.fromJSON(object.radioViewEvent) } : isSet7(object.radioHoverEvent) ? { $case: "radioHoverEvent", radioHoverEvent: RadioHoverEvent.fromJSON(object.radioHoverEvent) } : isSet7(object.inputViewEvent) ? { $case: "inputViewEvent", inputViewEvent: InputViewEvent.fromJSON(object.inputViewEvent) } : isSet7(object.inputHoverEvent) ? { $case: "inputHoverEvent", inputHoverEvent: InputHoverEvent.fromJSON(object.inputHoverEvent) } : isSet7(object.inputBlurEvent) ? { $case: "inputBlurEvent", inputBlurEvent: InputBlurEvent.fromJSON(object.inputBlurEvent) } : isSet7(object.inputFocusEvent) ? { $case: "inputFocusEvent", inputFocusEvent: InputFocusEvent.fromJSON(object.inputFocusEvent) } : isSet7(object.accordionClickEvent) ? {
        $case: "accordionClickEvent",
        accordionClickEvent: AccordionClickEvent.fromJSON(object.accordionClickEvent)
      } : isSet7(object.accordionViewEvent) ? { $case: "accordionViewEvent", accordionViewEvent: AccordionViewEvent.fromJSON(object.accordionViewEvent) } : isSet7(object.accordionHoverEvent) ? {
        $case: "accordionHoverEvent",
        accordionHoverEvent: AccordionHoverEvent.fromJSON(object.accordionHoverEvent)
      } : isSet7(object.boxViewEvent) ? { $case: "boxViewEvent", boxViewEvent: BoxViewEvent.fromJSON(object.boxViewEvent) } : isSet7(object.cardViewEvent) ? { $case: "cardViewEvent", cardViewEvent: CardViewEvent.fromJSON(object.cardViewEvent) } : isSet7(object.textViewEvent) ? { $case: "textViewEvent", textViewEvent: TextViewEvent.fromJSON(object.textViewEvent) } : isSet7(object.imageViewEvent) ? { $case: "imageViewEvent", imageViewEvent: ImageViewEvent.fromJSON(object.imageViewEvent) } : isSet7(object.pageViewEvent) ? { $case: "pageViewEvent", pageViewEvent: PageViewEvent.fromJSON(object.pageViewEvent) } : isSet7(object.sellDirectConversionEvent) ? {
        $case: "sellDirectConversionEvent",
        sellDirectConversionEvent: SellDirectConversionEvent.fromJSON(object.sellDirectConversionEvent)
      } : isSet7(object.externalLeadGeneratedEvent) ? {
        $case: "externalLeadGeneratedEvent",
        externalLeadGeneratedEvent: ExternalLeadGeneratedEvent.fromJSON(object.externalLeadGeneratedEvent)
      } : isSet7(object.sellerInputEvent) ? { $case: "sellerInputEvent", sellerInputEvent: SellerInputEvent.fromJSON(object.sellerInputEvent) } : isSet7(object.leadQualifiedEvent) ? { $case: "leadQualifiedEvent", leadQualifiedEvent: LeadQualifiedEvent.fromJSON(object.leadQualifiedEvent) } : isSet7(object.partnerEvent) ? { $case: "partnerEvent", partnerEvent: PartnerEvent.fromJSON(object.partnerEvent) } : isSet7(object.outreachEvent) ? { $case: "outreachEvent", outreachEvent: OutreachEvent.fromJSON(object.outreachEvent) } : isSet7(object.smsEvent) ? { $case: "smsEvent", smsEvent: SmsEvent.fromJSON(object.smsEvent) } : isSet7(object.buyerEvent) ? { $case: "buyerEvent", buyerEvent: BuyerEvent.fromJSON(object.buyerEvent) } : isSet7(object.authenticationEvent) ? {
        $case: "authenticationEvent",
        authenticationEvent: AuthenticationEvent.fromJSON(object.authenticationEvent)
      } : void 0,
      customerContext: isSet7(object.customerContext) ? CustomerContext.fromJSON(object.customerContext) : void 0,
      campaignContext: isSet7(object.campaignContext) ? CampaignContext.fromJSON(object.campaignContext) : void 0,
      exclusiveOffersContext: isSet7(object.exclusiveOffersContext) ? ExclusiveOffersContext.fromJSON(object.exclusiveOffersContext) : void 0,
      leadContext: isSet7(object.leadContext) ? LeadContext.fromJSON(object.leadContext) : void 0,
      sellDirectContext: isSet7(object.sellDirectContext) ? SellDirectContext.fromJSON(object.sellDirectContext) : void 0,
      adClickContext: isSet7(object.adClickContext) ? AdClickContext.fromJSON(object.adClickContext) : void 0
    };
  },
  toJSON(message) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A, _B, _C, _D, _E, _F, _G, _H, _I, _J, _K;
    const obj = {};
    if (message.uuid !== "") {
      obj.uuid = message.uuid;
    }
    if (message.timestamp !== void 0) {
      obj.timestamp = message.timestamp.toISOString();
    }
    if (message.anonymousId !== void 0) {
      obj.anonymousId = message.anonymousId;
    }
    if (message.humanId !== void 0) {
      obj.humanId = message.humanId;
    }
    if (((_a = message.source) == null ? void 0 : _a.$case) === "websiteSource") {
      obj.websiteSource = WebsiteSource.toJSON(message.source.websiteSource);
    }
    if (((_b = message.source) == null ? void 0 : _b.$case) === "mobileSource") {
      obj.mobileSource = MobileSource.toJSON(message.source.mobileSource);
    }
    if (((_c = message.source) == null ? void 0 : _c.$case) === "backendSource") {
      obj.backendSource = BackendSource.toJSON(message.source.backendSource);
    }
    if (((_d = message.source) == null ? void 0 : _d.$case) === "smsSource") {
      obj.smsSource = SmsSource.toJSON(message.source.smsSource);
    }
    if (((_e = message.event) == null ? void 0 : _e.$case) === "buttonClickEvent") {
      obj.buttonClickEvent = ButtonClickEvent.toJSON(message.event.buttonClickEvent);
    }
    if (((_f = message.event) == null ? void 0 : _f.$case) === "buttonViewEvent") {
      obj.buttonViewEvent = ButtonViewEvent.toJSON(message.event.buttonViewEvent);
    }
    if (((_g = message.event) == null ? void 0 : _g.$case) === "buttonHoverEvent") {
      obj.buttonHoverEvent = ButtonHoverEvent.toJSON(message.event.buttonHoverEvent);
    }
    if (((_h = message.event) == null ? void 0 : _h.$case) === "linkClickEvent") {
      obj.linkClickEvent = LinkClickEvent.toJSON(message.event.linkClickEvent);
    }
    if (((_i = message.event) == null ? void 0 : _i.$case) === "linkViewEvent") {
      obj.linkViewEvent = LinkViewEvent.toJSON(message.event.linkViewEvent);
    }
    if (((_j = message.event) == null ? void 0 : _j.$case) === "linkHoverEvent") {
      obj.linkHoverEvent = LinkHoverEvent.toJSON(message.event.linkHoverEvent);
    }
    if (((_k = message.event) == null ? void 0 : _k.$case) === "checkboxClickEvent") {
      obj.checkboxClickEvent = CheckboxClickEvent.toJSON(message.event.checkboxClickEvent);
    }
    if (((_l = message.event) == null ? void 0 : _l.$case) === "checkboxViewEvent") {
      obj.checkboxViewEvent = CheckboxViewEvent.toJSON(message.event.checkboxViewEvent);
    }
    if (((_m = message.event) == null ? void 0 : _m.$case) === "checkboxHoverEvent") {
      obj.checkboxHoverEvent = CheckboxHoverEvent.toJSON(message.event.checkboxHoverEvent);
    }
    if (((_n = message.event) == null ? void 0 : _n.$case) === "radioClickEvent") {
      obj.radioClickEvent = RadioClickEvent.toJSON(message.event.radioClickEvent);
    }
    if (((_o = message.event) == null ? void 0 : _o.$case) === "radioViewEvent") {
      obj.radioViewEvent = RadioViewEvent.toJSON(message.event.radioViewEvent);
    }
    if (((_p = message.event) == null ? void 0 : _p.$case) === "radioHoverEvent") {
      obj.radioHoverEvent = RadioHoverEvent.toJSON(message.event.radioHoverEvent);
    }
    if (((_q = message.event) == null ? void 0 : _q.$case) === "inputViewEvent") {
      obj.inputViewEvent = InputViewEvent.toJSON(message.event.inputViewEvent);
    }
    if (((_r = message.event) == null ? void 0 : _r.$case) === "inputHoverEvent") {
      obj.inputHoverEvent = InputHoverEvent.toJSON(message.event.inputHoverEvent);
    }
    if (((_s = message.event) == null ? void 0 : _s.$case) === "inputBlurEvent") {
      obj.inputBlurEvent = InputBlurEvent.toJSON(message.event.inputBlurEvent);
    }
    if (((_t = message.event) == null ? void 0 : _t.$case) === "inputFocusEvent") {
      obj.inputFocusEvent = InputFocusEvent.toJSON(message.event.inputFocusEvent);
    }
    if (((_u = message.event) == null ? void 0 : _u.$case) === "accordionClickEvent") {
      obj.accordionClickEvent = AccordionClickEvent.toJSON(message.event.accordionClickEvent);
    }
    if (((_v = message.event) == null ? void 0 : _v.$case) === "accordionViewEvent") {
      obj.accordionViewEvent = AccordionViewEvent.toJSON(message.event.accordionViewEvent);
    }
    if (((_w = message.event) == null ? void 0 : _w.$case) === "accordionHoverEvent") {
      obj.accordionHoverEvent = AccordionHoverEvent.toJSON(message.event.accordionHoverEvent);
    }
    if (((_x = message.event) == null ? void 0 : _x.$case) === "boxViewEvent") {
      obj.boxViewEvent = BoxViewEvent.toJSON(message.event.boxViewEvent);
    }
    if (((_y = message.event) == null ? void 0 : _y.$case) === "cardViewEvent") {
      obj.cardViewEvent = CardViewEvent.toJSON(message.event.cardViewEvent);
    }
    if (((_z = message.event) == null ? void 0 : _z.$case) === "textViewEvent") {
      obj.textViewEvent = TextViewEvent.toJSON(message.event.textViewEvent);
    }
    if (((_A = message.event) == null ? void 0 : _A.$case) === "imageViewEvent") {
      obj.imageViewEvent = ImageViewEvent.toJSON(message.event.imageViewEvent);
    }
    if (((_B = message.event) == null ? void 0 : _B.$case) === "pageViewEvent") {
      obj.pageViewEvent = PageViewEvent.toJSON(message.event.pageViewEvent);
    }
    if (((_C = message.event) == null ? void 0 : _C.$case) === "sellDirectConversionEvent") {
      obj.sellDirectConversionEvent = SellDirectConversionEvent.toJSON(message.event.sellDirectConversionEvent);
    }
    if (((_D = message.event) == null ? void 0 : _D.$case) === "externalLeadGeneratedEvent") {
      obj.externalLeadGeneratedEvent = ExternalLeadGeneratedEvent.toJSON(message.event.externalLeadGeneratedEvent);
    }
    if (((_E = message.event) == null ? void 0 : _E.$case) === "sellerInputEvent") {
      obj.sellerInputEvent = SellerInputEvent.toJSON(message.event.sellerInputEvent);
    }
    if (((_F = message.event) == null ? void 0 : _F.$case) === "leadQualifiedEvent") {
      obj.leadQualifiedEvent = LeadQualifiedEvent.toJSON(message.event.leadQualifiedEvent);
    }
    if (((_G = message.event) == null ? void 0 : _G.$case) === "partnerEvent") {
      obj.partnerEvent = PartnerEvent.toJSON(message.event.partnerEvent);
    }
    if (((_H = message.event) == null ? void 0 : _H.$case) === "outreachEvent") {
      obj.outreachEvent = OutreachEvent.toJSON(message.event.outreachEvent);
    }
    if (((_I = message.event) == null ? void 0 : _I.$case) === "smsEvent") {
      obj.smsEvent = SmsEvent.toJSON(message.event.smsEvent);
    }
    if (((_J = message.event) == null ? void 0 : _J.$case) === "buyerEvent") {
      obj.buyerEvent = BuyerEvent.toJSON(message.event.buyerEvent);
    }
    if (((_K = message.event) == null ? void 0 : _K.$case) === "authenticationEvent") {
      obj.authenticationEvent = AuthenticationEvent.toJSON(message.event.authenticationEvent);
    }
    if (message.customerContext !== void 0) {
      obj.customerContext = CustomerContext.toJSON(message.customerContext);
    }
    if (message.campaignContext !== void 0) {
      obj.campaignContext = CampaignContext.toJSON(message.campaignContext);
    }
    if (message.exclusiveOffersContext !== void 0) {
      obj.exclusiveOffersContext = ExclusiveOffersContext.toJSON(message.exclusiveOffersContext);
    }
    if (message.leadContext !== void 0) {
      obj.leadContext = LeadContext.toJSON(message.leadContext);
    }
    if (message.sellDirectContext !== void 0) {
      obj.sellDirectContext = SellDirectContext.toJSON(message.sellDirectContext);
    }
    if (message.adClickContext !== void 0) {
      obj.adClickContext = AdClickContext.toJSON(message.adClickContext);
    }
    return obj;
  },
  create(base) {
    return UserEvent.fromPartial(base ?? {});
  },
  fromPartial(object) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _A, _B, _C, _D, _E, _F, _G, _H, _I, _J, _K, _L, _M, _N, _O, _P, _Q, _R, _S, _T, _U, _V, _W, _X, _Y, _Z, __, _$, _aa, _ba, _ca, _da, _ea, _fa, _ga, _ha, _ia, _ja, _ka, _la, _ma, _na, _oa, _pa, _qa, _ra, _sa, _ta, _ua, _va, _wa, _xa, _ya, _za, _Aa, _Ba, _Ca, _Da, _Ea, _Fa, _Ga, _Ha, _Ia, _Ja, _Ka, _La, _Ma, _Na, _Oa, _Pa, _Qa, _Ra, _Sa, _Ta, _Ua, _Va, _Wa, _Xa, _Ya, _Za, __a, _$a, _ab, _bb, _cb;
    const message = createBaseUserEvent();
    message.uuid = object.uuid ?? "";
    message.timestamp = object.timestamp ?? void 0;
    message.anonymousId = object.anonymousId ?? void 0;
    message.humanId = object.humanId ?? void 0;
    if (((_a = object.source) == null ? void 0 : _a.$case) === "websiteSource" && ((_b = object.source) == null ? void 0 : _b.websiteSource) !== void 0 && ((_c = object.source) == null ? void 0 : _c.websiteSource) !== null) {
      message.source = {
        $case: "websiteSource",
        websiteSource: WebsiteSource.fromPartial(object.source.websiteSource)
      };
    }
    if (((_d = object.source) == null ? void 0 : _d.$case) === "mobileSource" && ((_e = object.source) == null ? void 0 : _e.mobileSource) !== void 0 && ((_f = object.source) == null ? void 0 : _f.mobileSource) !== null) {
      message.source = { $case: "mobileSource", mobileSource: MobileSource.fromPartial(object.source.mobileSource) };
    }
    if (((_g = object.source) == null ? void 0 : _g.$case) === "backendSource" && ((_h = object.source) == null ? void 0 : _h.backendSource) !== void 0 && ((_i = object.source) == null ? void 0 : _i.backendSource) !== null) {
      message.source = {
        $case: "backendSource",
        backendSource: BackendSource.fromPartial(object.source.backendSource)
      };
    }
    if (((_j = object.source) == null ? void 0 : _j.$case) === "smsSource" && ((_k = object.source) == null ? void 0 : _k.smsSource) !== void 0 && ((_l = object.source) == null ? void 0 : _l.smsSource) !== null) {
      message.source = { $case: "smsSource", smsSource: SmsSource.fromPartial(object.source.smsSource) };
    }
    if (((_m = object.event) == null ? void 0 : _m.$case) === "buttonClickEvent" && ((_n = object.event) == null ? void 0 : _n.buttonClickEvent) !== void 0 && ((_o = object.event) == null ? void 0 : _o.buttonClickEvent) !== null) {
      message.event = {
        $case: "buttonClickEvent",
        buttonClickEvent: ButtonClickEvent.fromPartial(object.event.buttonClickEvent)
      };
    }
    if (((_p = object.event) == null ? void 0 : _p.$case) === "buttonViewEvent" && ((_q = object.event) == null ? void 0 : _q.buttonViewEvent) !== void 0 && ((_r = object.event) == null ? void 0 : _r.buttonViewEvent) !== null) {
      message.event = {
        $case: "buttonViewEvent",
        buttonViewEvent: ButtonViewEvent.fromPartial(object.event.buttonViewEvent)
      };
    }
    if (((_s = object.event) == null ? void 0 : _s.$case) === "buttonHoverEvent" && ((_t = object.event) == null ? void 0 : _t.buttonHoverEvent) !== void 0 && ((_u = object.event) == null ? void 0 : _u.buttonHoverEvent) !== null) {
      message.event = {
        $case: "buttonHoverEvent",
        buttonHoverEvent: ButtonHoverEvent.fromPartial(object.event.buttonHoverEvent)
      };
    }
    if (((_v = object.event) == null ? void 0 : _v.$case) === "linkClickEvent" && ((_w = object.event) == null ? void 0 : _w.linkClickEvent) !== void 0 && ((_x = object.event) == null ? void 0 : _x.linkClickEvent) !== null) {
      message.event = {
        $case: "linkClickEvent",
        linkClickEvent: LinkClickEvent.fromPartial(object.event.linkClickEvent)
      };
    }
    if (((_y = object.event) == null ? void 0 : _y.$case) === "linkViewEvent" && ((_z = object.event) == null ? void 0 : _z.linkViewEvent) !== void 0 && ((_A = object.event) == null ? void 0 : _A.linkViewEvent) !== null) {
      message.event = { $case: "linkViewEvent", linkViewEvent: LinkViewEvent.fromPartial(object.event.linkViewEvent) };
    }
    if (((_B = object.event) == null ? void 0 : _B.$case) === "linkHoverEvent" && ((_C = object.event) == null ? void 0 : _C.linkHoverEvent) !== void 0 && ((_D = object.event) == null ? void 0 : _D.linkHoverEvent) !== null) {
      message.event = {
        $case: "linkHoverEvent",
        linkHoverEvent: LinkHoverEvent.fromPartial(object.event.linkHoverEvent)
      };
    }
    if (((_E = object.event) == null ? void 0 : _E.$case) === "checkboxClickEvent" && ((_F = object.event) == null ? void 0 : _F.checkboxClickEvent) !== void 0 && ((_G = object.event) == null ? void 0 : _G.checkboxClickEvent) !== null) {
      message.event = {
        $case: "checkboxClickEvent",
        checkboxClickEvent: CheckboxClickEvent.fromPartial(object.event.checkboxClickEvent)
      };
    }
    if (((_H = object.event) == null ? void 0 : _H.$case) === "checkboxViewEvent" && ((_I = object.event) == null ? void 0 : _I.checkboxViewEvent) !== void 0 && ((_J = object.event) == null ? void 0 : _J.checkboxViewEvent) !== null) {
      message.event = {
        $case: "checkboxViewEvent",
        checkboxViewEvent: CheckboxViewEvent.fromPartial(object.event.checkboxViewEvent)
      };
    }
    if (((_K = object.event) == null ? void 0 : _K.$case) === "checkboxHoverEvent" && ((_L = object.event) == null ? void 0 : _L.checkboxHoverEvent) !== void 0 && ((_M = object.event) == null ? void 0 : _M.checkboxHoverEvent) !== null) {
      message.event = {
        $case: "checkboxHoverEvent",
        checkboxHoverEvent: CheckboxHoverEvent.fromPartial(object.event.checkboxHoverEvent)
      };
    }
    if (((_N = object.event) == null ? void 0 : _N.$case) === "radioClickEvent" && ((_O = object.event) == null ? void 0 : _O.radioClickEvent) !== void 0 && ((_P = object.event) == null ? void 0 : _P.radioClickEvent) !== null) {
      message.event = {
        $case: "radioClickEvent",
        radioClickEvent: RadioClickEvent.fromPartial(object.event.radioClickEvent)
      };
    }
    if (((_Q = object.event) == null ? void 0 : _Q.$case) === "radioViewEvent" && ((_R = object.event) == null ? void 0 : _R.radioViewEvent) !== void 0 && ((_S = object.event) == null ? void 0 : _S.radioViewEvent) !== null) {
      message.event = {
        $case: "radioViewEvent",
        radioViewEvent: RadioViewEvent.fromPartial(object.event.radioViewEvent)
      };
    }
    if (((_T = object.event) == null ? void 0 : _T.$case) === "radioHoverEvent" && ((_U = object.event) == null ? void 0 : _U.radioHoverEvent) !== void 0 && ((_V = object.event) == null ? void 0 : _V.radioHoverEvent) !== null) {
      message.event = {
        $case: "radioHoverEvent",
        radioHoverEvent: RadioHoverEvent.fromPartial(object.event.radioHoverEvent)
      };
    }
    if (((_W = object.event) == null ? void 0 : _W.$case) === "inputViewEvent" && ((_X = object.event) == null ? void 0 : _X.inputViewEvent) !== void 0 && ((_Y = object.event) == null ? void 0 : _Y.inputViewEvent) !== null) {
      message.event = {
        $case: "inputViewEvent",
        inputViewEvent: InputViewEvent.fromPartial(object.event.inputViewEvent)
      };
    }
    if (((_Z = object.event) == null ? void 0 : _Z.$case) === "inputHoverEvent" && ((__ = object.event) == null ? void 0 : __.inputHoverEvent) !== void 0 && ((_$ = object.event) == null ? void 0 : _$.inputHoverEvent) !== null) {
      message.event = {
        $case: "inputHoverEvent",
        inputHoverEvent: InputHoverEvent.fromPartial(object.event.inputHoverEvent)
      };
    }
    if (((_aa = object.event) == null ? void 0 : _aa.$case) === "inputBlurEvent" && ((_ba = object.event) == null ? void 0 : _ba.inputBlurEvent) !== void 0 && ((_ca = object.event) == null ? void 0 : _ca.inputBlurEvent) !== null) {
      message.event = {
        $case: "inputBlurEvent",
        inputBlurEvent: InputBlurEvent.fromPartial(object.event.inputBlurEvent)
      };
    }
    if (((_da = object.event) == null ? void 0 : _da.$case) === "inputFocusEvent" && ((_ea = object.event) == null ? void 0 : _ea.inputFocusEvent) !== void 0 && ((_fa = object.event) == null ? void 0 : _fa.inputFocusEvent) !== null) {
      message.event = {
        $case: "inputFocusEvent",
        inputFocusEvent: InputFocusEvent.fromPartial(object.event.inputFocusEvent)
      };
    }
    if (((_ga = object.event) == null ? void 0 : _ga.$case) === "accordionClickEvent" && ((_ha = object.event) == null ? void 0 : _ha.accordionClickEvent) !== void 0 && ((_ia = object.event) == null ? void 0 : _ia.accordionClickEvent) !== null) {
      message.event = {
        $case: "accordionClickEvent",
        accordionClickEvent: AccordionClickEvent.fromPartial(object.event.accordionClickEvent)
      };
    }
    if (((_ja = object.event) == null ? void 0 : _ja.$case) === "accordionViewEvent" && ((_ka = object.event) == null ? void 0 : _ka.accordionViewEvent) !== void 0 && ((_la = object.event) == null ? void 0 : _la.accordionViewEvent) !== null) {
      message.event = {
        $case: "accordionViewEvent",
        accordionViewEvent: AccordionViewEvent.fromPartial(object.event.accordionViewEvent)
      };
    }
    if (((_ma = object.event) == null ? void 0 : _ma.$case) === "accordionHoverEvent" && ((_na = object.event) == null ? void 0 : _na.accordionHoverEvent) !== void 0 && ((_oa = object.event) == null ? void 0 : _oa.accordionHoverEvent) !== null) {
      message.event = {
        $case: "accordionHoverEvent",
        accordionHoverEvent: AccordionHoverEvent.fromPartial(object.event.accordionHoverEvent)
      };
    }
    if (((_pa = object.event) == null ? void 0 : _pa.$case) === "boxViewEvent" && ((_qa = object.event) == null ? void 0 : _qa.boxViewEvent) !== void 0 && ((_ra = object.event) == null ? void 0 : _ra.boxViewEvent) !== null) {
      message.event = { $case: "boxViewEvent", boxViewEvent: BoxViewEvent.fromPartial(object.event.boxViewEvent) };
    }
    if (((_sa = object.event) == null ? void 0 : _sa.$case) === "cardViewEvent" && ((_ta = object.event) == null ? void 0 : _ta.cardViewEvent) !== void 0 && ((_ua = object.event) == null ? void 0 : _ua.cardViewEvent) !== null) {
      message.event = { $case: "cardViewEvent", cardViewEvent: CardViewEvent.fromPartial(object.event.cardViewEvent) };
    }
    if (((_va = object.event) == null ? void 0 : _va.$case) === "textViewEvent" && ((_wa = object.event) == null ? void 0 : _wa.textViewEvent) !== void 0 && ((_xa = object.event) == null ? void 0 : _xa.textViewEvent) !== null) {
      message.event = { $case: "textViewEvent", textViewEvent: TextViewEvent.fromPartial(object.event.textViewEvent) };
    }
    if (((_ya = object.event) == null ? void 0 : _ya.$case) === "imageViewEvent" && ((_za = object.event) == null ? void 0 : _za.imageViewEvent) !== void 0 && ((_Aa = object.event) == null ? void 0 : _Aa.imageViewEvent) !== null) {
      message.event = {
        $case: "imageViewEvent",
        imageViewEvent: ImageViewEvent.fromPartial(object.event.imageViewEvent)
      };
    }
    if (((_Ba = object.event) == null ? void 0 : _Ba.$case) === "pageViewEvent" && ((_Ca = object.event) == null ? void 0 : _Ca.pageViewEvent) !== void 0 && ((_Da = object.event) == null ? void 0 : _Da.pageViewEvent) !== null) {
      message.event = { $case: "pageViewEvent", pageViewEvent: PageViewEvent.fromPartial(object.event.pageViewEvent) };
    }
    if (((_Ea = object.event) == null ? void 0 : _Ea.$case) === "sellDirectConversionEvent" && ((_Fa = object.event) == null ? void 0 : _Fa.sellDirectConversionEvent) !== void 0 && ((_Ga = object.event) == null ? void 0 : _Ga.sellDirectConversionEvent) !== null) {
      message.event = {
        $case: "sellDirectConversionEvent",
        sellDirectConversionEvent: SellDirectConversionEvent.fromPartial(object.event.sellDirectConversionEvent)
      };
    }
    if (((_Ha = object.event) == null ? void 0 : _Ha.$case) === "externalLeadGeneratedEvent" && ((_Ia = object.event) == null ? void 0 : _Ia.externalLeadGeneratedEvent) !== void 0 && ((_Ja = object.event) == null ? void 0 : _Ja.externalLeadGeneratedEvent) !== null) {
      message.event = {
        $case: "externalLeadGeneratedEvent",
        externalLeadGeneratedEvent: ExternalLeadGeneratedEvent.fromPartial(object.event.externalLeadGeneratedEvent)
      };
    }
    if (((_Ka = object.event) == null ? void 0 : _Ka.$case) === "sellerInputEvent" && ((_La = object.event) == null ? void 0 : _La.sellerInputEvent) !== void 0 && ((_Ma = object.event) == null ? void 0 : _Ma.sellerInputEvent) !== null) {
      message.event = {
        $case: "sellerInputEvent",
        sellerInputEvent: SellerInputEvent.fromPartial(object.event.sellerInputEvent)
      };
    }
    if (((_Na = object.event) == null ? void 0 : _Na.$case) === "leadQualifiedEvent" && ((_Oa = object.event) == null ? void 0 : _Oa.leadQualifiedEvent) !== void 0 && ((_Pa = object.event) == null ? void 0 : _Pa.leadQualifiedEvent) !== null) {
      message.event = {
        $case: "leadQualifiedEvent",
        leadQualifiedEvent: LeadQualifiedEvent.fromPartial(object.event.leadQualifiedEvent)
      };
    }
    if (((_Qa = object.event) == null ? void 0 : _Qa.$case) === "partnerEvent" && ((_Ra = object.event) == null ? void 0 : _Ra.partnerEvent) !== void 0 && ((_Sa = object.event) == null ? void 0 : _Sa.partnerEvent) !== null) {
      message.event = { $case: "partnerEvent", partnerEvent: PartnerEvent.fromPartial(object.event.partnerEvent) };
    }
    if (((_Ta = object.event) == null ? void 0 : _Ta.$case) === "outreachEvent" && ((_Ua = object.event) == null ? void 0 : _Ua.outreachEvent) !== void 0 && ((_Va = object.event) == null ? void 0 : _Va.outreachEvent) !== null) {
      message.event = { $case: "outreachEvent", outreachEvent: OutreachEvent.fromPartial(object.event.outreachEvent) };
    }
    if (((_Wa = object.event) == null ? void 0 : _Wa.$case) === "smsEvent" && ((_Xa = object.event) == null ? void 0 : _Xa.smsEvent) !== void 0 && ((_Ya = object.event) == null ? void 0 : _Ya.smsEvent) !== null) {
      message.event = { $case: "smsEvent", smsEvent: SmsEvent.fromPartial(object.event.smsEvent) };
    }
    if (((_Za = object.event) == null ? void 0 : _Za.$case) === "buyerEvent" && ((__a = object.event) == null ? void 0 : __a.buyerEvent) !== void 0 && ((_$a = object.event) == null ? void 0 : _$a.buyerEvent) !== null) {
      message.event = { $case: "buyerEvent", buyerEvent: BuyerEvent.fromPartial(object.event.buyerEvent) };
    }
    if (((_ab = object.event) == null ? void 0 : _ab.$case) === "authenticationEvent" && ((_bb = object.event) == null ? void 0 : _bb.authenticationEvent) !== void 0 && ((_cb = object.event) == null ? void 0 : _cb.authenticationEvent) !== null) {
      message.event = {
        $case: "authenticationEvent",
        authenticationEvent: AuthenticationEvent.fromPartial(object.event.authenticationEvent)
      };
    }
    message.customerContext = object.customerContext !== void 0 && object.customerContext !== null ? CustomerContext.fromPartial(object.customerContext) : void 0;
    message.campaignContext = object.campaignContext !== void 0 && object.campaignContext !== null ? CampaignContext.fromPartial(object.campaignContext) : void 0;
    message.exclusiveOffersContext = object.exclusiveOffersContext !== void 0 && object.exclusiveOffersContext !== null ? ExclusiveOffersContext.fromPartial(object.exclusiveOffersContext) : void 0;
    message.leadContext = object.leadContext !== void 0 && object.leadContext !== null ? LeadContext.fromPartial(object.leadContext) : void 0;
    message.sellDirectContext = object.sellDirectContext !== void 0 && object.sellDirectContext !== null ? SellDirectContext.fromPartial(object.sellDirectContext) : void 0;
    message.adClickContext = object.adClickContext !== void 0 && object.adClickContext !== null ? AdClickContext.fromPartial(object.adClickContext) : void 0;
    return message;
  }
};
function fromTimestamp(t) {
  let millis = (t.seconds || 0) * 1e3;
  millis += (t.nanos || 0) / 1e6;
  return new Date(millis);
}
function fromJsonTimestamp(o) {
  if (o instanceof Date) {
    return o;
  } else if (typeof o === "string") {
    return new Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}
function isSet7(value) {
  return value !== null && value !== void 0;
}

// src/internal/journey.ts
var BASE_JOURNEY_ENDPOINT = getDeployEnv() === "production" ? "https://journey.managed.services.opendoor.com/api/v1" : "https://journey-staging.managed.services.opendoor.com/api/v1";
var getJourneyEndpoint = (journeyEndpoint) => {
  if (journeyEndpoint) {
    return `${journeyEndpoint}/events`;
  } else {
    trackErrorInDatadog("the old journey endpoint is still being used", void 0, {
      data: {
        location: "observability"
      }
    });
    return `${BASE_JOURNEY_ENDPOINT}/events`;
  }
};
function initJourney(journeyEndpoint, batchInterval = 5e3) {
  if (typeof window === "undefined") {
    return {};
  }
  const userEventClient = new JourneyClientImpl({
    batchInterval,
    journeyEndpoint: `${BASE_JOURNEY_ENDPOINT}/user_event_batch`,
    jsonKey: "userEvents",
    maxNumRequest: 10
  });
  userEventClient.start();
  const eventWrapperClient = new JourneyClientImpl({
    batchInterval,
    journeyEndpoint: getJourneyEndpoint(journeyEndpoint),
    jsonKey: "eventWrappers",
    maxNumRequest: 20
  });
  eventWrapperClient.start();
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      userEventClient == null ? void 0 : userEventClient.send();
      eventWrapperClient == null ? void 0 : eventWrapperClient.send();
    }
  });
  return {
    userClient: userEventClient,
    eventClient: eventWrapperClient
  };
}
var getWebsiteSource = () => {
  let url = "";
  let language = [];
  let userAgent = "";
  let host = "";
  let pathname = "";
  let queryMap = {};
  let screen = void 0;
  if (typeof window !== "undefined") {
    url = window.location.href;
    userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
    language = typeof navigator !== "undefined" ? [...navigator.languages] : [];
    host = window.location.host;
    pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      queryMap[key] = queryMap[key] ? `${queryMap[key]},${value}` : value;
    });
    screen = {
      width: window.innerWidth,
      height: window.innerHeight,
      density: window.devicePixelRatio
    };
  }
  return {
    url,
    userAgent,
    host,
    pathname,
    queryMap,
    eventOrigin: 2 /* CLIENT */,
    proxyQueryMap: {},
    screen,
    language
  };
};
var trackUserEventInJourney = (userEvent, contextualData) => {
  var _a, _b, _c;
  const anonymousId = getAnonymousID();
  const websiteSource = getWebsiteSource();
  const event = { $case: Object.keys(userEvent)[0], ...userEvent };
  if (!anonymousId) {
    trackErrorInDatadog("ajs_anonymous_id is empty or undefined", void 0, {
      data: {
        location: "observability",
        anonymousId,
        event
      }
    });
  }
  const protobufUserEvent = {
    uuid: "",
    // journey generates this
    timestamp: /* @__PURE__ */ new Date(),
    anonymousId,
    humanId: (_a = contextualData.user) == null ? void 0 : _a.humanID,
    // extend when we add mobile
    source: {
      $case: "websiteSource",
      websiteSource
    },
    event,
    adClickContext: contextualData.adClickContext,
    campaignContext: contextualData.campaignContext,
    customerContext: contextualData.customerContext,
    exclusiveOffersContext: contextualData.exclusiveOffersContext,
    leadContext: contextualData.leadContext,
    sellDirectContext: contextualData.sellDirectContext
  };
  (_c = (_b = globalObservability.getJourneyUserClient()).enqueue) == null ? void 0 : _c.call(_b, JSON.stringify(UserEvent.toJSON(protobufUserEvent)));
};
var trackJourneyEventInJourney = (event, contextualData, journeyEventHelpers, sendJourneyEventsInDev) => {
  var _a, _b, _c;
  const anonymousId = getAnonymousID();
  const websiteSource = getWebsiteSource();
  if (!anonymousId) {
    if (sendJourneyEventsInDev) {
      console.log(
        "Please copy your odAuthAid cookie value and set it as the ajs_anonymous_id cookie value for your localhost to send journey events"
      );
    } else {
      trackErrorInDatadog("ajs_anonymous_id is empty or undefined", void 0, {
        data: {
          location: "observability",
          anonymousId,
          event
        }
      });
    }
  }
  const protobufJourneyEvent = {
    timestamp: /* @__PURE__ */ new Date(),
    source: {
      $case: "websiteSource",
      websiteSource
    },
    anonymousId,
    customerUuid: (_a = contextualData.customerContext) == null ? void 0 : _a.customerUuid,
    campaignContext: contextualData.campaignContext,
    category: event.category
  };
  const json = JSON.stringify(journeyEventHelpers.toJSON(protobufJourneyEvent));
  (_c = (_b = globalObservability.getJourneyEventClient()).enqueue) == null ? void 0 : _c.call(_b, json);
};
var getAnonymousID = () => {
  let anonymousID = Cookies.get("ajs_anonymous_id");
  if (!anonymousID) {
    return void 0;
  }
  return anonymousID;
};
var MAX_PAYLOAD_SIZE = 512;
function computeSizeInKilobytes(buffer) {
  const size = buffer.map((s) => s.length).reduce((prev, cur) => prev + cur, 0);
  return size / 1024;
}
function isApproachingBatchAPILimit(buffer) {
  return computeSizeInKilobytes(buffer) >= MAX_PAYLOAD_SIZE - 50;
}
var fetchWithRetry = async (url, init) => {
  var _a, _b;
  let retries = 5;
  const retryDelay = 500;
  let response = void 0;
  while (retries > 0) {
    try {
      response = await fetch(url, init);
      const statusCode = response.status;
      if (statusCode === 202) {
        return;
      }
      if (statusCode >= 500 && retries >= 1) {
        await new Promise((r) => setTimeout(r, retryDelay));
        retries = retries - 1;
      } else {
        break;
      }
    } catch (err) {
      if (retries >= 1) {
        await new Promise((r) => setTimeout(r, retryDelay));
        retries = retries - 1;
      }
    }
  }
  if ((_a = init.headers) == null ? void 0 : _a.toString().includes("Puppeteer")) {
    return;
  }
  const sentry = globalObservability.getSentryClient();
  (_b = sentry.withScope) == null ? void 0 : _b.call(sentry, (scope) => {
    var _a2;
    scope.setExtra("body", init.body);
    (_a2 = sentry.captureException) == null ? void 0 : _a2.call(
      sentry,
      `failed to track events in journey,statusCode: ${response == null ? void 0 : response.status}, response available: ${!!response}`
    );
  });
};
var JourneyClientImpl = class {
  buffer;
  batchInterval;
  journeyEndpoint;
  jsonKey;
  maxNumRequest;
  timer;
  constructor({ batchInterval, journeyEndpoint, jsonKey, maxNumRequest }) {
    this.buffer = [];
    this.batchInterval = batchInterval;
    this.journeyEndpoint = journeyEndpoint;
    this.jsonKey = jsonKey;
    this.maxNumRequest = maxNumRequest;
  }
  start() {
    this.timer = setInterval(() => this.send(), this.batchInterval);
  }
  stop() {
    this.timer && clearInterval(this.timer);
  }
  send() {
    if (this.buffer.length === 0) {
      return;
    }
    fetchWithRetry(this.journeyEndpoint, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Journey-Client-Name": "observability",
        "X-Journey-Client-Version": "1.0"
      },
      // Using keepalive:true ensures that the request is made even if the page is being unloaded.
      // There are actually benefits to using keep-alive true: https://www.stackpath.com/edge-academy/what-is-keep-alive/
      keepalive: true,
      method: "POST",
      body: `{"${this.jsonKey}":[${this.buffer.join(",")}]}`
    });
    this.buffer = [];
  }
  enqueue(json) {
    this.buffer.push(json);
    if (this.buffer.length >= this.maxNumRequest || isApproachingBatchAPILimit(this.buffer)) {
      this.send();
    }
  }
};

// src/internal/segment.ts
var SEGMENT_METHODS = [
  "trackSubmit",
  "trackClick",
  "trackLink",
  "trackForm",
  "pageview",
  "identify",
  "reset",
  "group",
  "track",
  "ready",
  "alias",
  "debug",
  "page",
  "once",
  "off",
  "on",
  "addSourceMiddleware",
  "addIntegrationMiddleware",
  "setAnonymousId",
  "addDestinationMiddleware"
];
var RESET_PASSWORD_TOKEN_REGEX = /reset_password_token=[a-zA-Z0-9_\-.]+?(?=&|$)/;
var ACCESS_TOKEN_REGEX = /access_token=[a-zA-Z0-9_\-.]+?(?=&|$)/;
if (typeof window !== "undefined") {
  const segment = [];
  window.analytics = window.analytics || segment;
  segment.factory = function(method) {
    return function() {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(method);
      segment.push(args);
      return segment;
    };
  };
  for (i = 0; i < SEGMENT_METHODS.length; i++) {
    key = SEGMENT_METHODS[i];
    segment[key] = segment.factory(key);
  }
  segment.SNIPPET_VERSION = "4.15.2";
  segment._loadOptions = { user: { localStorageFallbackDisabled: true } };
  segment.page({
    url: window.location.href.replace(RESET_PASSWORD_TOKEN_REGEX, "reset_password_token=REDACTED").replace(ACCESS_TOKEN_REGEX, "access_token=REDACTED"),
    search: window.location.search.replace(RESET_PASSWORD_TOKEN_REGEX, "reset_password_token=REDACTED").replace(ACCESS_TOKEN_REGEX, "access_token=REDACTED")
  });
}
var key;
var i;
var segmentWrapper = {};
SEGMENT_METHODS.forEach((methodName) => {
  segmentWrapper[methodName] = async function(...args) {
    var _a, _b, _c, _d, _e;
    try {
      await window.analytics[methodName](...args);
    } catch (e) {
      let error = new Error("Error sending to Segment");
      let extraErrorData = {
        method: methodName
      };
      if (methodName === "track") {
        error = new Error("Failed to send Segment Event or Error");
        extraErrorData["event"] = args[0];
        extraErrorData["value"] = (_a = args[1]) == null ? void 0 : _a.value;
        extraErrorData["team"] = ((_b = args[1]) == null ? void 0 : _b.team) || "";
        extraErrorData["pathname"] = ((_c = args[1]) == null ? void 0 : _c.pathname) || "";
      }
      (_e = (_d = globalObservability.getSentryClient()).withScope) == null ? void 0 : _e.call(_d, (scope) => {
        var _a2, _b2;
        scope.setExtras({
          error: e,
          ...extraErrorData
        });
        (_b2 = (_a2 = globalObservability.getSentryClient()).captureException) == null ? void 0 : _b2.call(_a2, error);
      });
    }
  };
});
var loadSegment = (key, lazyLoad) => {
  const injectScript = () => {
    var script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    if (key) {
      script.src = ("https:" === document.location.protocol ? "https://" : "http://") + "trkcdn.opendoor.com/analytics.js/v1/" + key + "/analytics.min.js";
    } else {
      script.src = ("https:" === document.location.protocol ? "https://" : "http://") + (getDeployEnv() === "production" ? "www.opendoor.com" : "demo.simplersell.com") + "/_scripts/sgm.min.js";
    }
    var first = document.getElementsByTagName("script")[0];
    first.parentNode.insertBefore(script, first);
  };
  if (lazyLoad) {
    window.addEventListener("load", injectScript);
  } else {
    injectScript();
  }
};
var segment_default = segmentWrapper;

// src/constants.ts
var UTM_MEDIUM = "utm_medium";
var UTM_SOURCE = "utm_source";
var UTM_CAMPAIGN = "utm_campaign";
var UTM_CONTENT = "utm_content";
var UTM_TERM = "utm_term";

// src/internal/analytics.ts
var analytics_exports = {};
__export(analytics_exports, {
  register: () => register,
  registerWithUserID: () => registerWithUserID,
  trackEvent: () => trackEvent,
  trackPageView: () => trackPageView
});

// src/internal/isQuotaExceededError.ts
var isQuotaExceededError = (e) => {
  let quotaExceeded = false;
  if (e) {
    if (e.code) {
      switch (e.code) {
        case 22:
          quotaExceeded = true;
          break;
        case 1014:
          if (e.name === "NS_ERROR_DOM_QUOTA_REACHED") {
            quotaExceeded = true;
          }
          break;
        default:
          break;
      }
    } else if (e.number === -2147024882) {
      quotaExceeded = true;
    }
  }
  return quotaExceeded;
};

// src/internal/analytics.ts
var LOG_LEVEL_CONFIG_NAME = "opendoor_log_level";
var log = (..._args) => {
  if (typeof window !== "undefined" && window.localStorage.getItem(LOG_LEVEL_CONFIG_NAME) === "true") {
    console.log(..._args);
  }
};
var wrapQuotaError = (cb) => {
  var _a, _b;
  try {
    cb();
  } catch (e) {
    if (!isQuotaExceededError(e)) {
      (_b = (_a = globalObservability.getSentryClient()).captureException) == null ? void 0 : _b.call(_a, e);
    }
  }
};
var register = (traits) => {
  if (typeof ga === "function" && typeof traits === "object") {
    ga("set", traits);
  }
  segment_default.identify(traits);
  if (getDeployEnv() !== "production") {
    log(`[DEBUG] Analytics.register: 
${JSON.stringify(traits, null, 2)}`);
  }
};
var registerWithUserID = (userId, traits) => {
  if (typeof ga === "function" && typeof traits === "object") {
    ga("set", traits);
  }
  segment_default.identify(userId, traits);
  if (getDeployEnv() !== "production") {
    log(`[DEBUG] Analytics.register: 
${userId} ${JSON.stringify(traits, null, 2)}`);
  }
};
var trackEvent = (category, action, value, extra) => {
  wrapQuotaError(() => {
    if (typeof ga === "function") {
      ga("send", "event", category, action, value, extra);
    }
  });
  wrapQuotaError(() => {
    segment_default.track(`${category}-${action}`, {
      channel: "web",
      domain: window.location.host,
      screen: {
        height: window.screen.height,
        width: window.screen.width
      },
      value,
      ...extra
    });
  });
  wrapQuotaError(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: `${category}.${action}.${value}`, extra });
  });
  if (getDeployEnv() !== "production") {
    log(
      `[DEBUG] Analytics.trackEvent: 
${category}-${action} ${JSON.stringify(
        {
          value,
          ...extra
        },
        null,
        2
      )}`
    );
  }
};
var matchInAppQueryParam = (queryParams) => {
  const fragments = queryParams && queryParams.match(/app\=(\d+)/);
  if (!fragments) {
    return false;
  }
  return !!fragments[1];
};
var trackPageView = (extra) => {
  const location = window.location;
  const pathname = location.pathname;
  const search = location.search;
  trackEvent("page", "view", "page", {
    app: matchInAppQueryParam(search),
    pathname,
    search,
    ...extra
  });
};

// src/observability-base.ts
var RealObservability = class {
  constructor(eventTrackingCategory, journeyEventHelpers) {
    this.eventTrackingCategory = eventTrackingCategory;
    this.journeyEventHelpers = journeyEventHelpers;
  }
  sentryClient = {};
  datadogRumClient = {};
  journeyUserClient = {};
  journeyEventClient = {};
  setSentryClient(sentryClient) {
    this.sentryClient = sentryClient;
  }
  getSentryClient() {
    return this.sentryClient;
  }
  setJourneyUserClient(journeyClient) {
    this.journeyUserClient = journeyClient;
  }
  setJourneyEventClient(journeyClient) {
    this.journeyEventClient = journeyClient;
  }
  getJourneyUserClient() {
    return this.journeyUserClient;
  }
  getJourneyEventClient() {
    return this.journeyEventClient;
  }
  setDatadogRumClient(datadogRumClient) {
    this.datadogRumClient = datadogRumClient;
  }
  getDatadogRumClient() {
    return this.datadogRumClient;
  }
  /**
   * Updates contextual data to add important information that might not always be passed through Context
   * This currently adds campaign context from the search params
   */
  updateJourneyContextualData(contextualData = emptyJourneyEventContextualData) {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParameters = [UTM_MEDIUM, UTM_SOURCE, UTM_CAMPAIGN, UTM_CONTENT, UTM_TERM];
      if (utmParameters.some((param) => searchParams.get(param))) {
        const campaignContext = {
          utmMedium: searchParams.get(UTM_MEDIUM) ?? void 0,
          utmSource: searchParams.get(UTM_SOURCE) ?? void 0,
          utmCampaign: searchParams.get(UTM_CAMPAIGN) ?? void 0,
          utmContent: searchParams.get(UTM_CONTENT) ?? void 0,
          utmTerm: searchParams.get(UTM_TERM) ?? void 0
        };
        return {
          ...contextualData,
          campaignContext
        };
      }
    }
    return contextualData;
  }
  /**
   * Updates contextual data for non-journey events
   * This will be removed when we remove old versions of tracking data
   */
  updateDeprecatedContextualData(contextualData = emptyContextualData) {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParameters = [UTM_MEDIUM, UTM_SOURCE, UTM_CAMPAIGN, UTM_CONTENT, UTM_TERM];
      if (utmParameters.some((param) => searchParams.get(param))) {
        const campaignContext = {
          utmMedium: searchParams.get(UTM_MEDIUM) ?? void 0,
          utmSource: searchParams.get(UTM_SOURCE) ?? void 0,
          utmCampaign: searchParams.get(UTM_CAMPAIGN) ?? void 0,
          utmContent: searchParams.get(UTM_CONTENT) ?? void 0,
          utmTerm: searchParams.get(UTM_TERM) ?? void 0
        };
        return {
          ...contextualData,
          campaignContext
        };
      }
    }
    return contextualData;
  }
  /**
   * Track events not performed by a user but meaningful internally. For events performed
   * by a user, use `trackUserEvent`.
   *
   * TODO (@core-experience-platform): rename to `trackInternalEvent`
   */
  trackEvent(action, label, contextualData = emptyContextualData, options) {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    contextualData = mergeContextualData(contextualData, getContextualDataFromDatadog());
    trackEvent(
      (options == null ? void 0 : options.categoryOverride) ?? this.eventTrackingCategory,
      action,
      label,
      contextualData.data
    );
    trackEventInDatadog(action, label, contextualData);
    trackEventInSentry(action, label, contextualData);
    if (options == null ? void 0 : options.addTiming) {
      trackPerformanceInDatadog(`${this.eventTrackingCategory}-${action}-${label}`);
    }
  }
  /**
   * @deprecated Use trackJourneyEvent instead
   * @see https://opendoor.atlassian.net/wiki/spaces/CONSUMER/pages/2283536444/How+to+Create+Journey+2.0+Events
   */
  trackUserEvent(event, contextualData = emptyContextualData) {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    trackUserEventInJourney(event, contextualData);
    trackUserEventInDatadog(event, contextualData);
    trackUserEventInSentry(event, contextualData);
  }
  trackJourneyEvent(categoryName, eventName, eventData, contextualData = emptyJourneyEventContextualData) {
    if (!this.journeyEventHelpers)
      throw new Error(
        "Provide journeyEventHelpers and EventWrapper types in initObservability to use trackJourneyEvent"
      );
    const eventWrapper = {
      category: {
        $case: categoryName,
        [categoryName]: {
          event: {
            $case: eventName,
            [eventName]: eventData
          }
        }
      }
    };
    contextualData = this.updateJourneyContextualData(contextualData);
    trackJourneyEventInJourney(
      eventWrapper,
      contextualData,
      this.journeyEventHelpers
    );
    trackJourneyEventInDatadog(
      eventWrapper,
      contextualData,
      this.journeyEventHelpers
    );
    trackJourneyEventInSentry(
      eventWrapper,
      contextualData,
      this.journeyEventHelpers
    );
  }
  trackError(label, error, contextualData = emptyContextualData, options) {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    contextualData = mergeContextualData(contextualData, getContextualDataFromDatadog());
    if (!(options == null ? void 0 : options.skipSentry)) {
      const sentryContextualData = trackErrorInSentry(label, error, contextualData);
      contextualData = mergeContextualData(contextualData, sentryContextualData);
    }
    trackErrorInDatadog(label, error, contextualData);
    trackEvent(this.eventTrackingCategory, "error", label, contextualData.data);
    if (options == null ? void 0 : options.addTiming) {
      trackPerformanceInDatadog(`${this.eventTrackingCategory}-error-${label}`);
    }
  }
};
var testonly = {
  initDebugObservability(opts = {}) {
    globalObservability.setCurrentInstance(new DebugObservability({ log: true, ...opts }));
  }
};
function useFakeObservability() {
  return {
    trackJourneyEvent: (categoryName, eventName, eventData) => {
      const eventWrapper = {
        category: {
          $case: categoryName,
          [categoryName]: {
            event: {
              $case: eventName,
              [eventName]: eventData
            }
          }
        }
      };
      console.info(`trackClientJourneyEvent(${JSON.stringify(eventWrapper)})`);
    }
  };
}
var DebugObservability = class {
  constructor(opts = {}, eventTrackingCategory, journeyEventHelpers) {
    this.eventTrackingCategory = eventTrackingCategory;
    this.journeyEventHelpers = journeyEventHelpers;
    this.sentryClient = (opts == null ? void 0 : opts.sentryClient) || {};
    this.journeyUserClient = (opts == null ? void 0 : opts.journeyUserClient) || {};
    this.journeyEventClient = (opts == null ? void 0 : opts.journeyEventClient) || {};
    this.datadogRumClient = (opts == null ? void 0 : opts.datadogRumClient) || {};
    this.log = (opts == null ? void 0 : opts.log) || false;
    this.sendJourneyEventsInDev = (opts == null ? void 0 : opts.sendJourneyEventsInDev) || false;
    this.env = opts == null ? void 0 : opts.env;
  }
  sentryClient = {};
  journeyUserClient = {};
  journeyEventClient = {};
  datadogRumClient = {};
  log = false;
  sendJourneyEventsInDev = false;
  env = getDeployEnv();
  /**
   * Updates contextual data to add important information that might not always be passed through Context
   * This currently adds campaign context from the search params
   */
  updateJourneyContextualData(contextualData = emptyJourneyEventContextualData) {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParameters = [UTM_MEDIUM, UTM_SOURCE, UTM_CAMPAIGN, UTM_CONTENT, UTM_TERM];
      if (utmParameters.some((param) => searchParams.get(param))) {
        const campaignContext = {
          utmMedium: searchParams.get(UTM_MEDIUM) ?? void 0,
          utmSource: searchParams.get(UTM_SOURCE) ?? void 0,
          utmCampaign: searchParams.get(UTM_CAMPAIGN) ?? void 0,
          utmContent: searchParams.get(UTM_CONTENT) ?? void 0,
          utmTerm: searchParams.get(UTM_TERM) ?? void 0
        };
        return {
          ...contextualData,
          campaignContext
        };
      }
    }
    return contextualData;
  }
  /**
   * Updates contextual data for non-journey events
   * This will be removed when we remove old versions of tracking data
   */
  updateDeprecatedContextualData(contextualData = emptyContextualData) {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParameters = [UTM_MEDIUM, UTM_SOURCE, UTM_CAMPAIGN, UTM_CONTENT, UTM_TERM];
      if (utmParameters.some((param) => searchParams.get(param))) {
        const campaignContext = {
          utmMedium: searchParams.get(UTM_MEDIUM) ?? void 0,
          utmSource: searchParams.get(UTM_SOURCE) ?? void 0,
          utmCampaign: searchParams.get(UTM_CAMPAIGN) ?? void 0,
          utmContent: searchParams.get(UTM_CONTENT) ?? void 0,
          utmTerm: searchParams.get(UTM_TERM) ?? void 0
        };
        return {
          ...contextualData,
          campaignContext
        };
      }
    }
    return contextualData;
  }
  trackEvent(action, label, contextualData = emptyContextualData, options) {
    if (this.log) {
      contextualData = this.updateDeprecatedContextualData(contextualData);
      const categoryOverride = (options == null ? void 0 : options.categoryOverride) ? `${options == null ? void 0 : options.categoryOverride}, ` : "";
      console.info(`trackEvent(${categoryOverride}${action}, ${label})`, contextualData);
    }
  }
  trackUserEvent(event, contextualData = emptyContextualData) {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    this.log && console.info(`trackUserEvent(${JSON.stringify(event)})`, contextualData);
  }
  trackJourneyEvent(categoryName, eventName, eventData, contextualData = emptyJourneyEventContextualData) {
    const eventWrapper = {
      category: {
        $case: categoryName,
        [categoryName]: {
          event: {
            $case: eventName,
            [eventName]: eventData
          }
        }
      }
    };
    contextualData = this.updateJourneyContextualData(contextualData);
    if (this.sendJourneyEventsInDev && this.env === "development") {
      if (!this.journeyEventHelpers)
        throw new Error(
          "Provide journeyEventHelpers and EventWrapper types in initObservability to use trackJourneyEvent"
        );
      trackJourneyEventInJourney(
        eventWrapper,
        contextualData,
        this.journeyEventHelpers,
        this.sendJourneyEventsInDev
      );
    }
    this.log && console.info(`trackJourneyEvent(${JSON.stringify(eventWrapper)})`, contextualData);
  }
  trackError(label, error, contextualData = emptyContextualData) {
    contextualData = this.updateDeprecatedContextualData(contextualData);
    this.log && console.error(`trackError(${label})`, error, contextualData);
  }
  setDatadogRumClient(datadogRumClient) {
    this.datadogRumClient = datadogRumClient;
  }
  getDatadogRumClient() {
    return this.datadogRumClient;
  }
  setJourneyUserClient(journeyClient) {
    this.journeyUserClient = journeyClient;
  }
  setJourneyEventClient(journeyClient) {
    this.journeyEventClient = journeyClient;
  }
  getJourneyUserClient() {
    return this.journeyUserClient;
  }
  getJourneyEventClient() {
    return this.journeyEventClient;
  }
  setSentClient(sentryClient) {
    this.sentryClient = sentryClient;
  }
  getSentryClient() {
    return this.sentryClient;
  }
};
var addDatadogRUMGlobalContext = (context) => {
  addRUMGlobalContext(context);
};

// src/react/ContextualDataContext.tsx
import React, { useContext } from "react";
var ContextualDataContext = React.createContext(emptyContextualData);
var useContextualData = () => {
  return useContext(ContextualDataContext);
};
var AddContextualData = ({
  children,
  user,
  data = {},
  campaignContext,
  customerContext,
  exclusiveOffersContext,
  leadContext,
  sellDirectContext
}) => {
  const parent = useContextualData();
  return /* @__PURE__ */ React.createElement(
    ContextualDataContext.Provider,
    {
      value: mergeContextualData(parent, {
        user,
        data,
        campaignContext,
        customerContext,
        exclusiveOffersContext,
        leadContext,
        sellDirectContext
      })
    },
    children
  );
};

// src/react/useObservability.ts
import { useEffect, useMemo, useRef } from "react";
function useObservability() {
  const env = getDeployEnv();
  const contextualData = useContextualData();
  const contextualDataRef = useRef(contextualData);
  useEffect(() => {
    contextualDataRef.current = contextualData;
  }, [contextualData]);
  return useMemo(
    () => ({
      /**
       * Track events not performed by a user but meaningful internally. For events performed
       * by a user, use `trackUserEvent`.
       *
       * TODO (@core-experience-platform): rename to `trackInternalEvent`
       */
      trackEvent: (action, label, data, options) => {
        if (!globalObservability.isInitialized()) {
          if (env === "production") {
            return;
          }
          throw new Error("Call initObservability before trackEvent");
        }
        globalObservability.trackEvent(
          action,
          label,
          mergeContextualData(contextualDataRef.current, { data }),
          options
        );
      },
      /**
       * @deprecated Use trackJourneyEvent instead
       * @see https://opendoor.atlassian.net/wiki/spaces/CONSUMER/pages/2283536444/How+to+Create+Journey+2.0+Events
       */
      trackUserEvent: (event) => {
        if (!globalObservability.isInitialized()) {
          if (env === "production") {
            return;
          }
          throw new Error("Call initObservability before trackUserEvent");
        }
        globalObservability.trackUserEvent(event, contextualDataRef.current);
      },
      trackJourneyEvent: (categoryName, eventName, eventData) => {
        if (!globalObservability.isInitialized()) {
          if (env === "production") {
            return;
          }
          throw new Error("Call initObservability before trackJourneyEvent");
        }
        globalObservability.trackJourneyEvent(
          categoryName,
          eventName,
          eventData,
          contextualDataRef.current
        );
      },
      trackError: (label, error, data, options) => {
        if (!globalObservability.isInitialized()) {
          if (env === "production") {
            return;
          }
          throw new Error("Call initObservability before trackError");
        }
        globalObservability.trackError(
          label,
          error,
          mergeContextualData(contextualDataRef.current, { data }),
          options
        );
      }
    }),
    [env]
  );
}

export {
  getDeployEnv,
  emptyContextualData,
  emptyJourneyEventContextualData,
  mergeContextualData,
  globalObservability,
  buildDefaultSentryOpts,
  buildDefaultDatadogOptions,
  loadGoogleTagManager,
  BASE_JOURNEY_ENDPOINT,
  initJourney,
  loadSegment,
  UTM_MEDIUM,
  UTM_SOURCE,
  UTM_CAMPAIGN,
  UTM_CONTENT,
  UTM_TERM,
  analytics_exports,
  RealObservability,
  testonly,
  useFakeObservability,
  DebugObservability,
  addDatadogRUMGlobalContext,
  ContextualDataContext,
  useContextualData,
  AddContextualData,
  useObservability
};
